import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Trophy, Eye, Clock, Copy, Users, CheckCircle, AlertCircle, Play, Shield, Crosshair, Zap, Award, Target, MapPin, Skull, DollarSign, Timer, XCircle, X, ChevronUp, ChevronDown, Hash, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import PasswordModal from '../modals/PasswordModal';
import MPTeamSelectionModal from './MPTeamSelectionModal';
import BRTeamJoinModal from './BRTeamJoinModal';
import TeamViewModal from './TeamViewModal';
import { BracketTournamentCard } from './BracketTournament';
import './TournamentsList.css';
import './TournamentAnimations.css';

// Helper function to get wait time warning
// Helper function to get weapon display names for Quick Soloconst getWeaponDisplayName = (weaponId) => {  const weaponNames = {    boltActionSniper: "🎯 Bolt Action Sniper",    shotgun: "💥 Shotgun",    assault: "🔫 Assault Rifle",    smg: "⚡ SMG"  };  return weaponNames[weaponId] || weaponId;};
const getWaitTimeWarning = (readyForApprovalAt, t) => {
  // Use readyForApprovalAt for Wait tab countdown (30 min from when lobby became full)
  // This is separate from the lobby timer which uses createdAt
  if (!readyForApprovalAt) {
    return { show: false };
  }
  
  const now = new Date();
  const readyAt = new Date(readyForApprovalAt);
  const diffMs = now - readyAt;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins >= 25) { // 25+ minutes - critical warning
    return {
      show: true,
      text: t('warningAutoCancel', '⚠️ {time}min until auto-cancel').replace('{time}', Math.max(0, 30 - diffMins)),
      critical: true
    };
  } else if (diffMins >= 20) { // 20+ minutes - warning
    return {
      show: true,
      text: t('warningTimeRemaining', '⏰ {time}min remaining').replace('{time}', 30 - diffMins),
      critical: false
    };
  }
  
  return { show: false };
};

// NEW: Cleanup Status Indicator Component
const CleanupStatusIndicator = ({ user, socket, t }) => {
  const [cleanupStatus, setCleanupStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (socket && ['main_admin', 'manager', 'supervisor', 'approver'].includes(user?.role)) {
      // Request cleanup status
      socket.emit('getTournamentCleanupStatus');

      const handleCleanupStatus = (data) => {
        setCleanupStatus(data.status);
      };

      const handleCleanupError = (error) => {
        // Suppress "service not available" errors since service works
        if (error.message !== 'Cleanup service not available') {
          console.error('Cleanup status error:', error);
        }
      };

      socket.on('cleanupStatus', handleCleanupStatus);
      socket.on('cleanupStatusError', handleCleanupError);

      return () => {
        socket.off('cleanupStatus', handleCleanupStatus);
        socket.off('cleanupStatusError', handleCleanupError);
      };
    }
  }, [socket, user]);

  const triggerManualCleanup = () => {
    if (user?.role !== 'main_admin') {
      toast.error(t('onlyMainAdminCanCleanup', 'Only main admins can trigger manual cleanup'));
      return;
    }

    setLoading(true);
    socket.emit('triggerManualCleanup');

    const handleCompleted = (data) => {
      setLoading(false);
      toast.success(
        `✅ ${t('manualCleanupCompleted', 'Manual cleanup completed: {deleted} tournaments removed')
          .replace('{deleted}', data.result.deleted)}`,
        { duration: 8000 }
      );
      socket.off('manualCleanupCompleted', handleCompleted);
      socket.off('manualCleanupError', handleError);
    };

    const handleError = (error) => {
      setLoading(false);
      toast.error(`❌ ${t('manualCleanupFailed', 'Manual cleanup failed')}: ${error.message}`);
      socket.off('manualCleanupCompleted', handleCompleted);
      socket.off('manualCleanupError', handleError);
    };

    socket.on('manualCleanupCompleted', handleCompleted);
    socket.on('manualCleanupError', handleError);
  };

  if (!cleanupStatus || !['main_admin', 'manager', 'supervisor', 'approver'].includes(user?.role)) {
    return null;
  }

  return (
    <div className="cleanup-status-indicator">
      <div className="cleanup-info">
        <span className="cleanup-label">🧹 {t('autoCleanup', 'Auto-Cleanup')}:</span>
        <span className={`cleanup-status ${cleanupStatus.isRunning ? 'running' : 'stopped'}`}>
          {cleanupStatus.isRunning ? t('active', 'Active') : t('stopped', 'Stopped')}
        </span>
        <span className="cleanup-details">
          ({t('removes', 'Removes')} 1h+ {t('old', 'old')})
        </span>
      </div>
      
      {user.role === 'main_admin' && (
        <button
          onClick={triggerManualCleanup}
          disabled={loading}
          className="manual-cleanup-btn"
        >
          {loading ? '...' : `🔄 ${t('cleanupNow', 'Cleanup Now')}`}
        </button>
      )}
    </div>
  );
};

const TournamentsList = ({
  tournaments,
  bracketTournaments = [],
  brMultistepTournaments = [],
  ffaMultistepTournaments = [],
  user,
  onTournamentUpdate,
  onBracketTournamentUpdate,
  onUserUpdate,
  t,
  socket,
  socketReady,
  currentLanguage,
  lastUpdateTime,
  setLastUpdateTime,
  loadTournaments,
  onOpenKickModal,
  cleanAdminName
}) => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ tournamentId: null, password: '', pendingTournament: null });
  const [expandedCreators, setExpandedCreators] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [countdowns, setCountdowns] = useState({});
  const [activeTab, setActiveTab] = useState('waiting');

  // Active bracket matches
  const [activeBracketMatches, setActiveBracketMatches] = useState([]);

  // UPDATED: Pagination and search for finished tab - increased limit to 1000
  const [finishedPage, setFinishedPage] = useState(1);
  const [finishedSearch, setFinishedSearch] = useState('');
  const FINISHED_PER_PAGE = 20;
  const MAX_FINISHED_TOURNAMENTS = 1000;

  // Cancel tournament state
  const [cancelModal, setCancelModal] = useState({
    show: false,
    tournament: null,
    reason: '',
    loading: false
  });

  // MP Team selection modal state
  const [mpTeamModal, setMpTeamModal] = useState({
    show: false,
    tournament: null,
    loading: false
  });

  // BR Team selection modal state (for BR Duo/Squad team preference)
  const [brTeamModal, setBrTeamModal] = useState({
    show: false,
    tournament: null,
    loading: false
  });

  // BR Team View modal state (for viewing/editing BR team preferences)
  const [brViewModal, setBrViewModal] = useState({
    show: false,
    tournament: null
  });

  // Fetch active bracket matches
  const loadActiveBracketMatches = async () => {
    try {
      const response = await axios.get('/api/bracket-tournaments/active-matches');
      setActiveBracketMatches(response.data || []);
    } catch (error) {
      console.error('Error loading active bracket matches:', error);
    }
  };

  // Load active bracket matches on mount and periodically
  useEffect(() => {
    loadActiveBracketMatches();
    const interval = setInterval(loadActiveBracketMatches, 5000);
    return () => clearInterval(interval);
  }, []);

  // FIXED: Enhanced socket event listeners with cleanup functionality
  useEffect(() => {
    if (socket && user && socketReady) {
      // Existing tournament update handler
      const handleTournamentUpdate = (data) => {
        setLastUpdateTime(Date.now());
        loadTournaments();
      };

      // Tournament deletion handler
      const handleTournamentDeleted = (data) => {
        if (data.autoCleanup) {
          // Auto-cleanup deletion
          toast.info(
            `🧹 ${t('oldTournamentRemoved', 'Old tournament automatically removed')}: ${data.tournamentName}`,
            {
              duration: 6000,
              style: {
                background: '#1f2937',
                color: '#fbbf24',
                border: '1px solid #f59e0b'
              }
            }
          );
          
          // Show refund info if user was affected
          if (data.refundInfo) {
            toast.success(
              `💰 ${data.refundInfo}`,
              {
                duration: 8000,
                style: {
                  background: '#1f2937',
                  color: '#10b981',
                  border: '1px solid #059669'
                }
              }
            );
          }
        } else {
          // Manual deletion
          toast.error(
            `🗑️ ${t('tournamentDeleted', 'Tournament deleted')}: ${data.tournamentName}`,
            {
              duration: 5000
            }
          );
        }
        
        // Refresh tournament data
        setLastUpdateTime(Date.now());
        loadTournaments();
      };

      // Tournaments cleaned up notification
      const handleTournamentsCleanedUp = (data) => {
        if (data.count > 0) {
          toast.info(
            `🧹 ${t('tournamentsAutoRemoved', '{count} old tournament(s) removed from lobby')
              .replace('{count}', data.count)}`,
            {
              duration: 8000,
              style: {
                background: '#1f2937',
                color: '#fbbf24',
                border: '1px solid #f59e0b'
              }
            }
          );
          
          if (data.totalRefunded > 0) {
            toast.success(
              `💰 ${t('pointsRefunded', '{amount} points refunded to players')
                .replace('{amount}', data.totalRefunded)}`,
              {
                duration: 6000,
                style: {
                  background: '#1f2937',
                  color: '#10b981',
                  border: '1px solid #059669'
                }
              }
            );
          }
          
          // Refresh data
          setLastUpdateTime(Date.now());
          loadTournaments();
          onUserUpdate(); // Update user points if they were refunded
        }
      };

      // Admin cleanup activity notification (admin only)
      const handleCleanupActivity = (data) => {
        if (['main_admin', 'manager', 'supervisor', 'approver'].includes(user.role)) {
          if (data.deleted > 0) {
            toast.info(
              `🧹 ${t('adminCleanupActivity', 'Cleanup: {deleted} tournaments removed, {refunded} points refunded')
                .replace('{deleted}', data.deleted)
                .replace('{refunded}', data.totalRefunded)}`,
              {
                duration: 10000,
                style: {
                  background: '#1f2937',
                  color: '#60a5fa',
                  border: '1px solid #3b82f6'
                }
              }
            );
          }
        }
      };

      // Manual cleanup notifications (admin only)
      const handleAdminManualCleanup = (data) => {
        if (['main_admin', 'manager', 'supervisor', 'approver'].includes(user.role)) {
          toast.info(
            `👨‍💼 ${t('manualCleanupTriggered', 'Manual cleanup by {admin}: {deleted} tournaments deleted')
              .replace('{admin}', data.triggeredBy)
              .replace('{deleted}', data.result.deleted)}`,
            {
              duration: 8000,
              style: {
                background: '#1f2937',
                color: '#a78bfa',
                border: '1px solid #8b5cf6'
              }
            }
          );
        }
      };

      // Existing role changed handler
      const handleRoleChanged = (data) => {
        if (data.username === user.username) {
          toast.success(t('roleUpdated', 'Your role has been updated!'));
          onUserUpdate();
        }
      };

      // Existing commission update handler
      const handleCommissionUpdate = (data) => {
        toast.success(t('commissionUpdated', 'Commission settings updated!'));
      };

      // Register all event listeners
      socket.on('tournamentUpdated', handleTournamentUpdate);
      socket.on('tournamentDeleted', handleTournamentDeleted);
      socket.on('tournamentsCleanedUp', handleTournamentsCleanedUp);
      socket.on('cleanupActivity', handleCleanupActivity);
      socket.on('adminManualCleanup', handleAdminManualCleanup);
      socket.on('roleChanged', handleRoleChanged);
      socket.on('commissionUpdate', handleCommissionUpdate);

      return () => {
        socket.off('tournamentUpdated', handleTournamentUpdate);
        socket.off('tournamentDeleted', handleTournamentDeleted);
        socket.off('tournamentsCleanedUp', handleTournamentsCleanedUp);
        socket.off('cleanupActivity', handleCleanupActivity);
        socket.off('adminManualCleanup', handleAdminManualCleanup);
        socket.off('roleChanged', handleRoleChanged);
        socket.off('commissionUpdate', handleCommissionUpdate);
      };
    }
  }, [socket, user, currentLanguage, socketReady, setLastUpdateTime, loadTournaments, onUserUpdate, t]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newCountdowns = {};
      tournaments.forEach(tournament => {
        if (tournament.status === 'countdown' && tournament.countdownStart) {
          const startTime = new Date(tournament.countdownStart).getTime();
          const now = new Date().getTime();
          const elapsed = now - startTime;
          const remaining = Math.max(0, (10 * 60 * 1000) - elapsed);
          
          if (remaining > 0) {
            const minutes = Math.floor(remaining / 60000);
            const seconds = Math.floor((remaining % 60000) / 1000);
            newCountdowns[tournament._id] = `${minutes}:${seconds.toString().padStart(2, '0')}`;
          } else {
            newCountdowns[tournament._id] = '0:00';
          }
        }
      });
      setCountdowns(newCountdowns);
    }, 1000);

    return () => clearInterval(interval);
  }, [tournaments]);

  // Add auto-cancellation event listener
  useEffect(() => {
    const handleTournamentCancelled = (data) => {
      if (data.type === 'auto_cancelled') {
        toast.error(t('tournamentAutoCancelled', 'Tournament auto-cancelled: {tournamentName}')
          .replace('{tournamentName}', data.tournamentName), {
          duration: 5000,
          style: {
            background: '#1f2937',
            color: '#fca5a5',
            border: '1px solid #ef4444'
          }
        });
        
        // Refresh tournament data
        onTournamentUpdate();
      }
    };

    if (socket) {
      socket.on('tournamentCancelled', handleTournamentCancelled);
      
      return () => {
        socket.off('tournamentCancelled', handleTournamentCancelled);
      };
    }
  }, [socket, onTournamentUpdate, t]);

  // Enhanced finished tournaments with search and pagination
  // Include completed bracket tournaments
  const finishedRegularTournaments = tournaments.filter(t => ['completed', 'cancelled'].includes(t.status));
  const finishedBracketTournaments = (bracketTournaments || [])
    .filter(t => t.status === 'completed')
    .map(t => ({ ...t, tournamentType: 'bracket' })); // Mark as bracket type
const finishedFFATournaments = (ffaMultistepTournaments || [])    .filter(t => ["completed", "cancelled"].includes(t.status) || t.ffaMultistepData?.ffaStatus === "completed")    .map(t => ({ ...t, tournamentType: "ffa_multistep" }));

  const finishedTournaments = [...finishedRegularTournaments, ...finishedBracketTournaments, ...finishedFFATournaments]
    .sort((a, b) => {
      // Sort by completion/cancellation date, most recent first
      const aDate = a.completedAt || a.cancellationInfo?.cancelledAt || a.createdAt;
      const bDate = b.completedAt || b.cancellationInfo?.cancelledAt || b.createdAt;
      return new Date(bDate) - new Date(aDate);
    })
    .slice(0, MAX_FINISHED_TOURNAMENTS);

  // Apply search filter for finished tournaments
  const filteredFinishedTournaments = finishedTournaments.filter(t => {
    if (!finishedSearch.trim()) return true;

    const searchTerm = finishedSearch.toLowerCase();
    const tournamentId = t._id.toLowerCase();
    const creatorName = cleanAdminName(t.creator?.inGameName || t.creator?.username || '')?.toLowerCase() || '';
    const gameMode = t.gameMode?.toLowerCase() || '';
    const map = t.map?.toLowerCase() || '';

    // For bracket tournaments, also search by prize pool
    const prizePool = t.tournamentType === 'bracket' ? String(t.prizePool || '') : '';

    return tournamentId.includes(searchTerm) ||
           creatorName.includes(searchTerm) ||
           gameMode.includes(searchTerm) ||
           map.includes(searchTerm) ||
           prizePool.includes(searchTerm);
  });

  // Calculate pagination for finished tournaments
  const finishedTotalPages = Math.ceil(filteredFinishedTournaments.length / FINISHED_PER_PAGE);
  const finishedStartIndex = (finishedPage - 1) * FINISHED_PER_PAGE;
  const paginatedFinishedTournaments = filteredFinishedTournaments.slice(
    finishedStartIndex, 
    finishedStartIndex + FINISHED_PER_PAGE
  );

  // Find user's active tournament (exclude bracket tournaments - they have their own display)
  // Find user active bracket match
  const userActiveBracketMatch = activeBracketMatches.find(match => {
    const userId = user._id?.toString?.() || user._id;
    return [...(match.team1?.players || []), ...(match.team2?.players || [])]
      .some(p => (p.userId?._id?.toString?.() || p.userId?.toString?.()) === userId);
  });

  const userActiveTournament = tournaments.find(tournament =>
    ['countdown', 'active'].includes(tournament.status) &&
    tournament.tournamentType !== 'bracket' && tournament.tournamentType !== 'br_multistep' &&
    tournament.participants.some(p => p._id === user._id)
  );
// Find user active BR Multistep tournament with active round
  const userActiveBRMultistep = brMultistepTournaments.find(tournament => {
    if (tournament.brMultistepData?.multistepStatus !== "in_progress") return false;
    const currentRound = tournament.brMultistepData?.currentRound || 0;
    const roundData = tournament.brMultistepData?.rounds?.[currentRound - 1];
    if (!roundData?.roomId) return false;
    const userId = user._id?.toString?.() || user._id;
    if (tournament.brMultistepData?.type === "solo") {
      return tournament.participants?.some(p => (p._id?.toString?.() || p._id) === userId);
    } else {
      return tournament.brMultistepData?.standings?.some(team =>
        team.teamMembers?.some(m => (m.userId?._id?.toString?.() || m.userId?.toString?.()) === userId)
      );
    }
  });

  // Check if user can see room ID (participant or admin)
  const canSeeRoomId = (tournament) => {
    // Admins can always see room ID
    if (['main_admin', 'manager', 'supervisor', 'approver'].includes(user.role)) {
      return true;
    }
    // Regular players can only see if they're participants
    return tournament.participants.some(p => p._id === user._id);
  };

  // Helper function to check if user can kick
  const canKickFromTournament = (tournament, user) => {
    // Creator can always kick
    if (tournament.creator._id === user._id) return true;
    
    // Admin/supervisor/approver can always kick
    if (['main_admin', 'manager', 'supervisor', 'approver'].includes(user.role)) return true;
    
    return false;
  };

  const joinTournament = async (tournamentId, password = '', mpTeamNumber = null, brTeamData = null, mpTeamSettings = null) => {
    if (loading) return;
    setLoading(true);
    try {
      const payload = { password };
      if (mpTeamNumber) {
        payload.mpTeamNumber = mpTeamNumber;
      }
      // Add MP team settings if provided (for MP team captain)
      if (mpTeamSettings) {
        payload.mpTeamPreference = mpTeamSettings.preference;
        payload.mpTeammates = mpTeamSettings.teammates || [];
      }
      // Add BR team data if provided (for BR Duo/Squad tournaments)
      if (brTeamData) {
        payload.brTeamPreference = brTeamData.brTeamPreference;
        payload.brSquadmates = brTeamData.brSquadmates || [];
        if (brTeamData.brTeamNumber) {
          payload.brTeamNumber = brTeamData.brTeamNumber;
        }
      }
      await axios.post(`/api/tournaments/${tournamentId}/join`, payload);
      toast.success(t('joinedTournament', 'Joined tournament successfully!'));
      onTournamentUpdate();
      onUserUpdate();
      setShowPasswordModal(false);
      setPasswordData({ tournamentId: null, password: '' });
      setMpTeamModal({ show: false, tournament: null, loading: false });
      setBrTeamModal({ show: false, tournament: null, loading: false });
    } catch (error) {
      // UPDATED: Handle kicked user error
      if (error.response?.data?.kicked) {
        toast.error(t('cannotJoinKicked', 'You have been kicked from this tournament and cannot rejoin'), {
          duration: 5000,
          style: {
            background: '#1f2937',
            color: '#fca5a5',
            border: '1px solid #ef4444'
          }
        });
      } else if (error.response?.data?.requiresTeamSelection) {
        // Team selection required - show modal
        toast.error(t('selectTeamFirst', 'Please select a team to join'));
      } else {
        const message = error.response?.data?.message || t('failedToJoinTournament', 'Failed to join tournament');
        toast.error(message);
      }
    } finally {
      setLoading(false);
      setMpTeamModal(prev => ({ ...prev, loading: false }));
      setBrTeamModal(prev => ({ ...prev, loading: false }));
    }
  };

  const leaveTournament = async (tournamentId) => {
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`/api/tournaments/${tournamentId}/leave`);
      toast.success(t('leftTournament', 'Left tournament successfully!'));
      onTournamentUpdate();
      onUserUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToLeaveTournament', 'Failed to leave tournament'));
    } finally {
      setLoading(false);
    }
  };

  // Cancel tournament handler
  const handleCancelTournament = async (tournament, reason) => {
    setCancelModal(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await axios.post(`/api/tournaments/${tournament._id}/cancel`, {
        reason: reason || t('tournamentCancelled', 'Tournament cancelled')
      });

      toast.success(t('tournamentCancelledSuccessfully', 'Tournament cancelled successfully. {refundInfo}')
        .replace('{refundInfo}', response.data.refundInfo || ''));
      setCancelModal({ show: false, tournament: null, reason: '', loading: false });
      
      // Refresh data
      onTournamentUpdate();
      onUserUpdate();

    } catch (error) {
      const message = error.response?.data?.message || t('failedToCancelTournament', 'Failed to cancel tournament');
      toast.error(message);
      setCancelModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Open cancel modal
  const openCancelModal = (tournament) => {
    setCancelModal({
      show: true,
      tournament,
      reason: '',
      loading: false
    });
  };

  // Close cancel modal
  const closeCancelModal = () => {
    setCancelModal({
      show: false,
      tournament: null,
      reason: '',
      loading: false
    });
  };

  // Check if user can cancel tournament
  const canCancelTournament = (tournament) => {
    // Main admin can cancel any tournament
    if (['main_admin', 'manager'].includes(user.role)) return true;
    
    // Supervisors and approvers can only cancel tournaments they approved
    if (['supervisor', 'approver'].includes(user.role)) {
      return tournament.approvedBy && tournament.approvedBy._id === user._id;
    }
    
    return false;
  };

  // Check if tournament is a multiplayer team game (Team 1 vs Team 2)
  const isMultiplayerTeamGame = (tournament) => {
    const teamGameModes = ['teamDeathmatch', 'domination', 'searchAndDestroy', 'hardpoint',
                           'killConfirmed', 'captureTheFlag', 'frontline', 'cranked'];
    return teamGameModes.includes(tournament.gameMode) && tournament.multiplayerType === 'team';
  };

  // Check if tournament is a Battle Royale Duo/Squad (requires team preference selection)
  const isBRDuoOrSquad = (tournament) => {
    return tournament.gameMode === 'battleRoyale' &&
           ['duo', 'squad'].includes(tournament.multiplayerType);
  };

  const handleJoinClick = (tournament) => {
    // UPDATED: Check password FIRST before any team selection modals
    if (tournament.password) {
      // Store the tournament for later processing after password is validated
      setPasswordData({
        tournamentId: tournament._id,
        password: '',
        pendingTournament: tournament
      });
      setShowPasswordModal(true);
      return;
    }

    // No password - check if this is a multiplayer team game - show team selection
    if (isMultiplayerTeamGame(tournament)) {
      setMpTeamModal({ show: true, tournament, loading: false });
      return;
    }

    // Check if this is a BR Duo/Squad - show BR team preference modal
    if (isBRDuoOrSquad(tournament)) {
      setBrTeamModal({ show: true, tournament, loading: false });
      return;
    }

    // No special requirements - join directly
    joinTournament(tournament._id);
  };

  // Handle after password is validated - check if team selection is needed
  const handlePasswordConfirmed = (password) => {
    const tournament = passwordData.pendingTournament;
    if (!tournament) {
      // Fallback - just join with password
      joinTournament(passwordData.tournamentId, password);
      return;
    }

    // Close password modal
    setShowPasswordModal(false);

    // Check if MP team selection is needed
    if (isMultiplayerTeamGame(tournament)) {
      setPasswordData(prev => ({ ...prev, password, pendingTournament: null }));
      setMpTeamModal({ show: true, tournament, loading: false, storedPassword: password });
      return;
    }

    // Check if BR team selection is needed
    if (isBRDuoOrSquad(tournament)) {
      setPasswordData(prev => ({ ...prev, password, pendingTournament: null }));
      setBrTeamModal({ show: true, tournament, loading: false, storedPassword: password });
      return;
    }

    // No team selection needed - join directly with password
    joinTournament(tournament._id, password);
    setPasswordData({ tournamentId: null, password: '', pendingTournament: null });
  };

  // Handle MP team selection (with optional captain settings)
  const handleMpTeamSelect = (teamNumber, teamPreference = null, teammates = []) => {
    if (!mpTeamModal.tournament) return;

    setMpTeamModal(prev => ({ ...prev, loading: true }));

    // Use stored password if available (password was already entered)
    const password = mpTeamModal.storedPassword || '';

    // Build MP team settings if provided (new captain joining empty team)
    const mpTeamSettings = teamPreference ? {
      preference: teamPreference,
      teammates: teammates
    } : null;

    // Join with team selection, password, and optional captain settings
    joinTournament(mpTeamModal.tournament._id, password, teamNumber, null, mpTeamSettings);
  };

  // Handle BR team preference selection
  const handleBRTeamJoin = (brTeamData) => {
    if (!brTeamModal.tournament) return;

    setBrTeamModal(prev => ({ ...prev, loading: true }));

    // Use stored password if available (password was already entered)
    const password = brTeamModal.storedPassword || '';

    // Join with BR team preference and password
    joinTournament(brTeamModal.tournament._id, password, null, brTeamData);
  };

  const copyMatchId = (matchId) => {
    if (!matchId) return toast.error(t('noMatchIdToCopy', 'No Match ID available to copy'));
    navigator.clipboard.writeText(matchId);
    toast.success(t('matchIdCopied', 'Match ID copied to clipboard!'));
  };

  // Copy tournament ID function
  const copyTournamentId = (tournamentId) => {
    if (!tournamentId) return toast.error(t('noTournamentIdToCopy', 'No Tournament ID available to copy'));
    navigator.clipboard.writeText(tournamentId);
    toast.success(t('tournamentIdCopied', 'Tournament ID copied to clipboard!'));
  };

  const categorizedTournaments = {
    // Filter out bracket tournaments from waiting/approval/ongoing - they have their own section
    waiting: tournaments.filter(t => t.status === 'waiting' && t.tournamentType !== 'bracket' && t.tournamentType !== 'br_multistep').sort((a, b) => {
      // Giveaways first
      if (a.tournamentType === 'giveaway' && b.tournamentType !== 'giveaway') return -1;
      if (a.tournamentType !== 'giveaway' && b.tournamentType === 'giveaway') return 1;
      return 0;
    }),
    approval: tournaments.filter(t => t.status === 'ready_for_approval' && t.tournamentType !== 'bracket' && t.tournamentType !== 'br_multistep').sort((a, b) => {
      // Giveaways first
      if (a.tournamentType === 'giveaway' && b.tournamentType !== 'giveaway') return -1;
      if (a.tournamentType !== 'giveaway' && b.tournamentType === 'giveaway') return 1;
      return 0;
    }),
    ongoing: tournaments.filter(t => ['countdown', 'active'].includes(t.status) && t.tournamentType !== 'bracket' && t.tournamentType !== 'br_multistep').sort((a, b) => {
      // Giveaways first
      if (a.tournamentType === 'giveaway' && b.tournamentType !== 'giveaway') return -1;
      if (a.tournamentType !== 'giveaway' && b.tournamentType === 'giveaway') return 1;
      return 0;
    }),
    // Enhanced finished section with pagination (includes bracket tournaments in chronological order)
    finished: paginatedFinishedTournaments
  };

  const getCurrentTournaments = () => categorizedTournaments[activeTab] || [];

  const groupByCreator = (tournaments) => {
    return tournaments.reduce((acc, tournament) => {
      const creatorId = tournament.creator._id;
      if (!acc[creatorId]) acc[creatorId] = [];
      acc[creatorId].push(tournament);
      return acc;
    }, {});
  };

  const currentTournaments = getCurrentTournaments();
  const tournamentsByCreator = activeTab === 'waiting' ? groupByCreator(currentTournaments) : null;

  // Enhanced empty state messages
  const getEmptyStateMessage = (activeTab) => {
    switch (activeTab) {
      case 'waiting':
        return {
          icon: <Users className="w-12 h-12 text-gray-600" />,
          title: t('noActiveLobies', 'NO ACTIVE LOBBIES'),
          subtitle: t('deployFirstTournament', 'Deploy the first tournament!')
        };
      case 'approval':
        return {
          icon: <AlertCircle className="w-12 h-12 text-gray-600" />,
          title: t('noPendingDeployments', 'NO PENDING DEPLOYMENTS'),
          subtitle: t('fullLobbiesAppear', 'Full lobbies will appear here (auto-cancel after 30min)')
        };
      case 'ongoing':
        return {
          icon: <Zap className="w-12 h-12 text-gray-600" />,
          title: t('noActiveOperations', 'NO ACTIVE OPERATIONS'),
          subtitle: t('deployedTournamentsAppear', 'Deployed tournaments will appear here')
        };
      case 'finished':
        return {
          icon: <Award className="w-12 h-12 text-gray-600" />,
          title: t('noCompletedMissions', 'NO COMPLETED MISSIONS'),
          subtitle: t('finishedCancelledAppear', 'Finished and cancelled tournaments will appear here')
        };
      default:
        return {
          icon: <AlertCircle className="w-12 h-12 text-gray-600" />,
          title: t('noData', 'NO DATA'),
          subtitle: t('nothingToShow', 'Nothing to show')
        };
    }
  };

  // Enhanced helper function to get tournament status display
  const getTournamentStatusDisplay = (tournament) => {
    if (tournament.status === 'cancelled') {
      const isAutoCancelled = tournament.cancellationInfo?.reason?.includes('Auto-cancelled');
      return {
        status: 'cancelled',
        label: isAutoCancelled ? t('autoCanceled', 'AUTO-CANCELED') : t('canceled', 'CANCELED'),
        color: '#ef4444',
        bgColor: 'rgba(239, 68, 68, 0.3)',
        isAuto: isAutoCancelled
      };
    } else if (tournament.status === 'completed') {
      const isGiveaway = tournament.tournamentType === 'giveaway';
      return {
        status: 'completed',
        label: t('completed', 'COMPLETED'),
        color: isGiveaway ? '#fbbf24' : '#22c55e',
        bgColor: isGiveaway ? 'rgba(251, 191, 36, 0.3)' : 'rgba(34, 197, 94, 0.2)',
        isGiveaway
      };
    }
    
    return {
      status: tournament.status,
      label: tournament.status.toUpperCase(),
      color: '#9ca3af',
      bgColor: 'rgba(107, 114, 128, 0.2)'
    };
  };

  const tabs = [
    { id: 'waiting', label: t('lobby', 'LOBBY'), icon: <Users className="w-4 h-4" />, count: categorizedTournaments.waiting.length, color: 'yellow' },
    { id: 'approval', label: t('wait', 'WAIT'), icon: <AlertCircle className="w-4 h-4" />, count: categorizedTournaments.approval.length, color: 'red' },
    { id: 'ongoing', label: t('active', 'ACTIVE'), icon: <Zap className="w-4 h-4" />, count: categorizedTournaments.ongoing.length + activeBracketMatches.length, color: 'orange' },
    { 
      id: 'finished', 
      label: t('finish', 'FINISH'), 
      icon: <Award className="w-4 h-4" />, 
      count: filteredFinishedTournaments.length,
      color: 'green',
      breakdown: {
        completed: finishedTournaments.filter(t => t.status === 'completed').length,
        cancelled: finishedTournaments.filter(t => t.status === 'cancelled').length,
        total: finishedTournaments.length
      }
    }
  ];

  const getModeDisplay = (tournament) => {
    const modeMap = {
      'teamDeathmatch': 'Team DeathMatch',
      'domination': 'Domination',
      'searchAndDestroy': 'Search&Destroy',
      'hardpoint': 'Hardpoint',
      'killConfirmed': 'Kill Confirmed',
      'captureTheFlag': 'Capture The Flag',
      'frontline': 'Frontline',
      'freeForAll': 'Free For All',
      'gunGame': 'Gun Game',
      'sticksAndStones': 'Sticks&Stones',
      'oneShotOneKill': 'One Shot One Kill',
      'cranked': 'Crank',
      'rapidFire': 'Rapid Fire',
      'attackOfTheUndead': 'Attack Of The Undead',
      'battleRoyale': 'Battle Royale'
    };

    const mode = modeMap[tournament.gameMode] || tournament.gameMode.toUpperCase();
    
    if (['teamDeathmatch', 'domination', 'searchAndDestroy', 'hardpoint', 'killConfirmed', 'captureTheFlag', 'frontline'].includes(tournament.gameMode)) {
      if (tournament.multiplayerType === 'team') return `${tournament.maxPlayers/2}v${tournament.maxPlayers/2} ${mode}`;
      if (tournament.multiplayerType === 'custom') return `${mode} CUSTOM`;
      return mode;
    } else if (tournament.gameMode === 'battleRoyale') {
      // Show BR type: Solo, Duo, or Squad
      const brType = tournament.multiplayerType === 'duo' ? 'Duo' :
                     tournament.multiplayerType === 'squad' ? 'Squad' : 'Solo';
      return `BR ${brType} ${tournament.maxPlayers} ${t('players', 'Players')}`;
    } else {
      return `${tournament.maxPlayers}P ${mode}`;
    }
  };

  const getCustomModeDisplay = (tournament) => {
    const modes = {
      snipersOnly: t('snipers', 'SNIPERS'),
      rifleOnly: t('rifles', 'RIFLES'), 
      knifeOnly: t('melee', 'MELEE'),
      executionOnly: t('execution', 'EXECUTION'),
      grenadesOnly: t('explosives', 'EXPLOSIVES')
    };
    
    const gameModes = {
      'teamDeathmatch': 'Team DeathMatch',
      'domination': 'Domination',
      'searchAndDestroy': 'Search&Destroy',
      'hardpoint': 'Hardpoint',
      'killConfirmed': 'Kill Confirmed',
      'captureTheFlag': 'Capture The Flag',
      'frontline': 'Frontline',
      'freeForAll': 'Free For All',
      'gunGame': 'Gun Game',
      'sticksAndStones': 'Sticks&Stones',
      'oneShotOneKill': 'One Shot One Kill',
      'cranked': 'Crank',
      'rapidFire': 'Rapid Fire',
      'attackOfTheUndead': 'Attack Of The Undead',
      'battleRoyale': 'Battle Royale'
    };
    
    return tournament.multiplayerType === 'custom' && tournament.customMode ? 
      modes[tournament.customMode] || gameModes[tournament.gameMode]?.toUpperCase() : 
      gameModes[tournament.gameMode]?.toUpperCase() || tournament.gameMode.toUpperCase();
  };

  // New function for desktop title display
  const getTitleDisplay = (tournament) => {
    const creatorName = cleanAdminName(tournament.creator?.inGameName || tournament.creator?.username || 'Unknown');
    if (tournament.multiplayerType === 'custom' && tournament.customMode) {
      return `${creatorName}'s ${t('custom', 'Custom')}`;
    }
    return `${creatorName}'s ${getCustomModeDisplay(tournament)}`;
  };

  // New function for custom mode warning display
  const getCustomModeWarning = (tournament) => {
    const modes = {
      snipersOnly: t('snipersOnly', 'SNIPERS ONLY'),
      rifleOnly: t('rifleOnly', 'RIFLES ONLY'), 
      knifeOnly: t('knifeOnly', 'MELEE ONLY'),
      executionOnly: t('executionOnly', 'EXECUTION ONLY'),
      grenadesOnly: t('grenadesOnly', 'EXPLOSIVES ONLY')
    };
    
    if (tournament.multiplayerType === 'custom' && tournament.customMode) {
      return modes[tournament.customMode] || '';
    }
    return '';
  };

  const getTimeSince = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 60) return `${diffMins}${t('mins', 'm')}`;
    if (diffHours < 24) return `${diffHours}${t('hrs', 'h')}`;
    return `${Math.floor(diffHours / 24)}${t('daysShort', 'd')}`;
  };

  return (
    // Add wrapper with conditional class for bottom spacing
    <div className={`tournament-list-main-wrapper ${userActiveTournament ? 'has-active-match' : ''}`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="tournament-list-header">
          <div className="tournament-list-header-content">
            <h2 className="tournament-list-title">
              <Zap className="w-5 h-5 text-yellow-500" />
              {t('tournamentOps', 'TOURNAMENT OPS')}
            </h2>
            
            <div className="header-actions">
              <CleanupStatusIndicator user={user} socket={socket} t={t} />
            </div>
          </div>
        </div>

        {/* Main Container */}
        <div className="tournament-list-container">
          {/* Tab Navigation */}
          <div className="tournament-tabs">
            <div className="tournament-tabs-container">
              {tabs.map(tab => (
                <button 
                  key={tab.id} 
                  onClick={() => setActiveTab(tab.id)}
                  className={`tournament-tab ${activeTab === tab.id ? 'active' : 'inactive'}`}
                >
                  {tab.icon}
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className={`tournament-tab-count ${activeTab === tab.id ? 'active' : 'inactive'}`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="tournament-list-content">
            {/* Search bar for finished tab */}
            {activeTab === 'finished' && (
              <div className="mb-4 p-3 bg-gray-800/30 rounded-lg border border-gray-700">
                <div className="flex items-center gap-3">
                  <Hash className="w-4 h-4 text-blue-400" />
                  <input
                    type="text"
                    placeholder={t('searchFinishedTournaments', 'Search by Tournament ID, Creator, Game Mode, or Map...')}
                    value={finishedSearch}
                    onChange={(e) => {
                      setFinishedSearch(e.target.value);
                      setFinishedPage(1);
                    }}
                    className="flex-1 bg-gray-700/50 border border-gray-600 rounded-md px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {finishedSearch && (
                    <button
                      onClick={() => {
                        setFinishedSearch('');
                        setFinishedPage(1);
                      }}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {finishedSearch && (
                  <div className="mt-2 text-sm text-gray-400">
                    {t('searchResults', 'Found {count} tournaments')
                      .replace('{count}', filteredFinishedTournaments.length)}
                  </div>
                )}
              </div>
            )}

            <div className="tournaments-grid">
              {activeTab === 'waiting' && (
                <>
                  {Object.keys(tournamentsByCreator).length === 0 ? (
                    <EmptyState {...getEmptyStateMessage('waiting')} />
                  ) : (
                    Object.entries(tournamentsByCreator).map(([creatorId, creatorTournaments]) => {
                      const primaryTournament = creatorTournaments[0];
                      const additionalTournaments = creatorTournaments.slice(1);
                      const isExpanded = expandedCreators.has(creatorId);

                      return (
                        <React.Fragment key={creatorId}>
                          <WaitingTournamentCard
                            tournament={primaryTournament}
                            user={user}
                            onJoin={handleJoinClick}
                            onLeave={leaveTournament}
                            loading={loading}
                            additionalCount={additionalTournaments.length}
                            isExpanded={isExpanded}
                            onToggleExpanded={() => {
                              const newExpanded = new Set(expandedCreators);
                              if (isExpanded) newExpanded.delete(creatorId);
                              else newExpanded.add(creatorId);
                              setExpandedCreators(newExpanded);
                            }}
                            getModeDisplay={getModeDisplay}
                            getCustomModeDisplay={getCustomModeDisplay}
                            getTitleDisplay={getTitleDisplay}
                            getCustomModeWarning={getCustomModeWarning}
                            getTimeSince={getTimeSince}
                            cleanAdminName={cleanAdminName}
                            onTournamentUpdate={onTournamentUpdate}
                            onUserUpdate={onUserUpdate}
                            onOpenKickModal={onOpenKickModal}
                            onOpenBRViewModal={(t) => setBrViewModal({ show: true, tournament: t })}
                            canKickFromTournament={canKickFromTournament}
                            t={t}
                          />
                          {isExpanded && additionalTournaments.map(tournament => (
                            <div key={tournament._id} className="tournament-card-additional">
                              <WaitingTournamentCard
                                tournament={tournament}
                                user={user}
                                onJoin={handleJoinClick}
                                onLeave={leaveTournament}
                                loading={loading}
                                isAdditional={true}
                                getModeDisplay={getModeDisplay}
                                getCustomModeDisplay={getCustomModeDisplay}
                                getTitleDisplay={getTitleDisplay}
                                getCustomModeWarning={getCustomModeWarning}
                                getTimeSince={getTimeSince}
                                cleanAdminName={cleanAdminName}
                                onTournamentUpdate={onTournamentUpdate}
                                onUserUpdate={onUserUpdate}
                                onOpenKickModal={onOpenKickModal}
                                onOpenBRViewModal={(t) => setBrViewModal({ show: true, tournament: t })}
                                canKickFromTournament={canKickFromTournament}
                                t={t}
                              />
                            </div>
                          ))}
                        </React.Fragment>
                      );
                    })
                  )}
                </>
              )}

              {activeTab === 'approval' && (
                <>
                  {categorizedTournaments.approval.length === 0 ? (
                    <EmptyState {...getEmptyStateMessage('approval')} />
                  ) : (
                    categorizedTournaments.approval.map(tournament => (
                      <ApprovalTournamentCard 
                        key={tournament._id} 
                        tournament={tournament} 
                        user={user} 
                        onCancel={canCancelTournament(tournament) ? openCancelModal : null}
                        getModeDisplay={getModeDisplay} 
                        getCustomModeDisplay={getCustomModeDisplay}
                        getTitleDisplay={getTitleDisplay}
                        getCustomModeWarning={getCustomModeWarning}
                        getTimeSince={getTimeSince} 
                        cleanAdminName={cleanAdminName}
                        t={t}
                      />
                    ))
                  )}
                </>
              )}

              {activeTab === 'ongoing' && (
                <>
                  {categorizedTournaments.ongoing.length === 0 && activeBracketMatches.length === 0 ? (
                    <EmptyState {...getEmptyStateMessage('ongoing')} />
                  ) : (
                    <>
                      {/* Active Bracket Matches */}
                      {activeBracketMatches.length > 0 && (
                        <div className="bracket-matches-section">
                          <div className="bracket-matches-header">
                            <Trophy className="w-5 h-5 text-green-400" />
                            <span>{t('activeBracketMatches', 'Active Bracket Matches')}</span>
                            <span className="bracket-match-count">{activeBracketMatches.length}</span>
                          </div>
                          <div className="bracket-matches-list">
                            {activeBracketMatches.map(match => (
                              <ActiveBracketMatchCard
                                key={`${match.tournamentId}-${match.matchId}`}
                                match={match}
                                user={user}
                                onCopyRoomId={copyMatchId}
                                t={t}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Regular ongoing tournaments */}
                      {categorizedTournaments.ongoing.map(tournament => (
                        <OngoingTournamentCard
                          key={tournament._id}
                          tournament={tournament}
                          user={user}
                          countdown={countdowns[tournament._id]}
                          onCopyMatchId={copyMatchId}
                          onTournamentUpdate={onTournamentUpdate}
                          onCancel={canCancelTournament(tournament) ? openCancelModal : null}
                          canSeeRoomId={canSeeRoomId(tournament)}
                          getModeDisplay={getModeDisplay}
                          getCustomModeDisplay={getCustomModeDisplay}
                          getTitleDisplay={getTitleDisplay}
                          getCustomModeWarning={getCustomModeWarning}
                          getTimeSince={getTimeSince}
                          cleanAdminName={cleanAdminName}
                          t={t}
                        />
                      ))}
                    </>
                  )}
                </>
              )}

              {activeTab === 'finished' && (
                <>
                  {categorizedTournaments.finished.length === 0 && filteredFinishedTournaments.length === 0 ? (
                    <EmptyState {...getEmptyStateMessage('finished')} />
                  ) : finishedSearch && filteredFinishedTournaments.length === 0 ? (
                    <div className="tournament-empty-state">
                      <Hash className="w-12 h-12 text-gray-600" />
                      <p className="tournament-empty-title">{t('noSearchResults', 'NO SEARCH RESULTS')}</p>
                      <p className="tournament-empty-subtitle">
                        {t('tryDifferentSearch', 'Try a different search term or clear the search')}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Render all finished tournaments in chronological order */}
                      {categorizedTournaments.finished.map(tournament => (
                        tournament.tournamentType === 'bracket' ? (
                          <BracketTournamentCard
                            key={tournament._id}
                            tournament={tournament}
                            user={user}
                            onUpdate={onBracketTournamentUpdate || onTournamentUpdate}
                            t={t}
                          />
                        ) : tournament.tournamentType === 'ffa_multistep' ? (
                          <div key={tournament._id} className="ffa-finished-card">
                            <div className="ffa-finished-header">
                              <span className="ffa-finished-type">🎯 FFA Tournament</span>
                              <span className="ffa-finished-status">{tournament.ffaMultistepData?.ffaStatus === 'completed' ? '✅ Completed' : '❌ Cancelled'}</span>
                            </div>
                            {tournament.ffaMultistepData?.grandWinner && (
                              <div className="ffa-finished-winner">
                                <span>🏆 Winner:</span>
                                <strong>{tournament.ffaMultistepData.grandWinner.inGameName || tournament.ffaMultistepData.grandWinner.username}</strong>
                              </div>
                            )}
                            <div className="ffa-finished-info">
                              <span>💰 Prize: {tournament.ffaMultistepData?.prizePool || 0} pts</span>
                              <span>👥 Players: {tournament.ffaMultistepData?.approvedPlayers?.length || 0}</span>
                            </div>
                          </div>
                        ) : (
                          <FinishedTournamentCard
                            key={tournament._id}
                            tournament={tournament}
                            user={user}
                            onCopyTournamentId={copyTournamentId}
                            getModeDisplay={getModeDisplay}
                            getCustomModeDisplay={getCustomModeDisplay}
                            getTitleDisplay={getTitleDisplay}
                            getCustomModeWarning={getCustomModeWarning}
                            cleanAdminName={cleanAdminName}
                            t={t}
                          />
                        )
                      ))}
                      
                      {/* Pagination Controls */}
                      {filteredFinishedTournaments.length > 0 && finishedTotalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-6 p-4 bg-gray-800/30 rounded-lg border border-gray-700 flex-wrap">
                          <button
                            onClick={() => setFinishedPage(Math.max(1, finishedPage - 1))}
                            disabled={finishedPage === 1}
                            className="px-3 py-1 bg-green-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700 transition-colors flex items-center gap-1"
                            title={t('previousPage', 'Previous page')}
                          >
                            <ChevronLeft className="w-3 h-3" />
                            {t('prev', 'Prev')}
                          </button>

                          {finishedPage > 3 && finishedTotalPages > 5 && (
                            <>
                              <button
                                onClick={() => setFinishedPage(1)}
                                className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
                              >
                                1
                              </button>
                              <span className="text-gray-400 px-1">...</span>
                            </>
                          )}

                          {Array.from({ length: finishedTotalPages }, (_, i) => i + 1)
                            .filter(page => {
                              return Math.abs(page - finishedPage) <= 1;
                            })
                            .map(page => (
                              <button
                                key={page}
                                onClick={() => setFinishedPage(page)}
                                className={`px-3 py-1 rounded transition-colors ${
                                  finishedPage === page
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-700 text-white hover:bg-gray-600'
                                }`}
                              >
                                {page}
                              </button>
                            ))}

                          {finishedPage < finishedTotalPages - 2 && finishedTotalPages > 5 && (
                            <>
                              <span className="text-gray-400 px-1">...</span>
                              <button
                                onClick={() => setFinishedPage(finishedTotalPages)}
                                className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
                              >
                                {finishedTotalPages}
                              </button>
                            </>
                          )}

                          <button
                            onClick={() => setFinishedPage(Math.min(finishedTotalPages, finishedPage + 1))}
                            disabled={finishedPage === finishedTotalPages}
                            className="px-3 py-1 bg-green-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700 transition-colors flex items-center gap-1"
                            title={t('nextPage', 'Next page')}
                          >
                            {t('next', 'Next')}
                            <ChevronRight className="w-3 h-3" />
                          </button>
                          
                          <div className="w-full text-center mt-2 text-sm text-gray-400">
                            {t('pageInfo', 'Page {current} of {total} ({totalItems} total)')
                              .replace('{current}', finishedPage)
                              .replace('{total}', finishedTotalPages)
                              .replace('{totalItems}', filteredFinishedTournaments.length)}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Cancel Tournament Modal */}
        {cancelModal.show && (
          <CancelTournamentModal
            tournament={cancelModal.tournament}
            reason={cancelModal.reason}
            setReason={(reason) => setCancelModal(prev => ({ ...prev, reason }))}
            onConfirm={() => handleCancelTournament(cancelModal.tournament, cancelModal.reason)}
            onCancel={closeCancelModal}
            loading={cancelModal.loading}
            cleanAdminName={cleanAdminName}
            t={t}
          />
        )}

        {showPasswordModal && (
          <PasswordModal
            onJoin={handlePasswordConfirmed}
            onClose={() => {
              setShowPasswordModal(false);
              setPasswordData({ tournamentId: null, password: '', pendingTournament: null });
            }}
            loading={loading}
            t={t}
          />
        )}

        {/* MP Team Selection Modal */}
        {mpTeamModal.show && mpTeamModal.tournament && (
          <MPTeamSelectionModal
            tournament={mpTeamModal.tournament}
            user={user}
            onSelectTeam={handleMpTeamSelect}
            onClose={() => setMpTeamModal({ show: false, tournament: null, loading: false })}
            loading={mpTeamModal.loading}
            t={t}
          />
        )}

        {/* BR Team Selection Modal (for Duo/Squad team preference) */}
        {brTeamModal.show && brTeamModal.tournament && (
          <BRTeamJoinModal
            tournament={brTeamModal.tournament}
            user={user}
            onJoin={handleBRTeamJoin}
            onClose={() => setBrTeamModal({ show: false, tournament: null, loading: false })}
            loading={brTeamModal.loading}
            t={t}
          />
        )}

        {/* Team View Modal (for viewing/editing BR or MP team positions) */}
        {brViewModal.show && brViewModal.tournament && (
          <TeamViewModal
            tournament={brViewModal.tournament}
            user={user}
            onClose={() => setBrViewModal({ show: false, tournament: null })}
            onUpdate={(updatedTournament) => {
              // Update the tournament in the list
              if (onTournamentUpdate) {
                onTournamentUpdate(updatedTournament);
              }
              // Also update the modal's tournament data
              setBrViewModal(prev => ({ ...prev, tournament: updatedTournament }));
            }}
            t={t}
          />
        )}
      </div>

      {/* Active Match Button - Fixed Bottom Right - OUTSIDE the scrollable container */}
      {userActiveTournament && (
        <ActiveMatchButton
          tournament={userActiveTournament}
          countdown={countdowns[userActiveTournament._id]}
          onCopyMatchId={copyMatchId}
          user={user}
          cleanAdminName={cleanAdminName}
          getModeDisplay={getModeDisplay}
          t={t}
        />
      )}

      {/* Active Bracket Match Button */}
      {userActiveBracketMatch && !userActiveTournament && (
        <ActiveBracketMatchButton
          match={userActiveBracketMatch}
          onCopyRoomId={copyMatchId}
          t={t}
        />
      )}

      {/* Active BR Multistep Button */}
      {userActiveBRMultistep && !userActiveTournament && !userActiveBracketMatch && (
        <ActiveBRMultistepButton
          tournament={userActiveBRMultistep}
          onCopyRoomId={copyMatchId}
          t={t}
        />
      )}
    </div>
  );
};

// Enhanced Active Match Button Component - FIXED positioning for all devices
const ActiveMatchButton = ({ tournament, countdown, onCopyMatchId, user, cleanAdminName, t, getModeDisplay }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [showTeamPlacements, setShowTeamPlacements] = useState(false);

  const isCountdown = tournament.status === 'countdown';
  const isActive = tournament.status === 'active';

  // Check game type
  const isBattleRoyale = tournament.gameMode === 'battleRoyale';
  const isBRTeamGame = isBattleRoyale && ['duo', 'squad'].includes(tournament.multiplayerType);
  const teamGameModes = ['teamDeathmatch', 'domination', 'searchAndDestroy', 'hardpoint', 'killConfirmed', 'captureTheFlag', 'frontline', 'cranked'];
  const isMpTeamGame = teamGameModes.includes(tournament.gameMode) && tournament.multiplayerType === 'team';

  // Get proper game mode display
  const getGameModeDisplay = () => {
    if (getModeDisplay) return getModeDisplay(tournament);
    if (isBattleRoyale) {
      const brType = tournament.multiplayerType || 'solo';
      return `Battle Royale (${brType.charAt(0).toUpperCase() + brType.slice(1)})`;
    }
    return tournament.gameMode;
  };

  // Get proper player format display
  const getPlayerFormatDisplay = () => {
    if (isBattleRoyale) {
      const brType = tournament.multiplayerType || 'solo';
      if (brType === 'solo') return `${tournament.maxPlayers} ${t('players', 'Players')}`;
      if (brType === 'duo') return `${tournament.maxPlayers / 2} ${t('duos', 'Duos')}`;
      if (brType === 'squad') return `${Math.ceil(tournament.maxPlayers / 4)} ${t('squads', 'Squads')}`;
    }
    if (isMpTeamGame) {
      return `${tournament.maxPlayers / 2}v${tournament.maxPlayers / 2}`;
    }
    return `${tournament.maxPlayers} ${t('players', 'Players')}`;
  };

  // Get BR teams
  const getBRTeams = () => {
    if (!isBRTeamGame || !tournament.brTeamAssignments) return [];
    const maxTeamSize = tournament.multiplayerType === 'duo' ? 2 : 4;
    const teamCount = Math.ceil(tournament.maxPlayers / maxTeamSize);
    const teams = [];
    for (let i = 0; i < teamCount; i++) {
      const teamNumber = i + 1;
      const members = (tournament.brTeamAssignments || [])
        .filter(a => a.teamNumber === teamNumber)
        .map(a => ({
          id: (a.userId?._id || a.userId)?.toString(),
          name: a.inGameName || a.username || 'Unknown',
          isFiller: a.isFiller
        }));
      if (members.length > 0) {
        teams.push({ teamNumber, members, maxSize: maxTeamSize });
      }
    }
    return teams;
  };

  // Get MP teams
  const getMPTeams = () => {
    if (!isMpTeamGame || !tournament.mpTeamAssignments) return { team1: [], team2: [] };
    const team1 = [], team2 = [];
    tournament.mpTeamAssignments.forEach(a => {
      const member = {
        id: (a.userId?._id || a.userId)?.toString(),
        name: a.inGameName || a.username || 'Unknown'
      };
      if (a.teamNumber === 1) team1.push(member);
      else if (a.teamNumber === 2) team2.push(member);
    });
    return { team1, team2 };
  };

  // Auto-hide logic for countdown tournaments after time expires
  useEffect(() => {
    if (isCountdown && countdown === '0:00') {
      const timer = setTimeout(() => setIsVisible(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isCountdown, countdown]);

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop for expanded view */}
      {isExpanded && (
        <div 
          className="active-match-backdrop"
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Main Button Container - Fixed positioning */}
      <div className="active-match-button-container">
        {/* Expanded Details Panel */}
        {isExpanded && (
          <div className="active-match-details-panel">
            <div className="active-match-details-header">
              <h3 className="active-match-details-title">{t('activeMatchDetails', 'Active Match Details')}</h3>
              <button 
                onClick={() => setIsExpanded(false)}
                className="active-match-details-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Match Info */}
            <div className="active-match-details-content">
              <div className="active-match-room-section">
                <div className="active-match-room-header">
                  <span className="active-match-room-label">{t('roomLabel', '🎮 Room ID')}</span>
                  <button 
                    onClick={() => onCopyMatchId(tournament.matchId)}
                    className="active-match-room-copy-btn"
                  >
                    <Copy className="w-3 h-3" />
                    {t('copy', 'COPY')}
                  </button>
                </div>
                <div className="active-match-room-id">{tournament.matchId}</div>
              </div>

              {/* Tournament Rules */}
              <div className="active-match-settings-section">
                <h4 className="active-match-settings-title">{t('matchSettings', '🏆 Match Settings')}</h4>
                <div className="active-match-settings-list">
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t('gameMode', 'Game Mode')}:</span>
                    <span className="active-match-setting-value">{getGameModeDisplay()}</span>
                  </div>
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t('players', 'Players')}:</span>
                    <span className="active-match-setting-value">{getPlayerFormatDisplay()}</span>
                  </div>
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t('map', 'Map')}:</span>
                    <span className="active-match-setting-value">{tournament.map}</span>
                  </div>
                  {tournament.maxKills && !isBattleRoyale && (
                    <div className="active-match-setting-item">
                      <span className="active-match-setting-label">{t('maxKills', 'Max Kills')}:</span>
                      <span className="active-match-setting-value">{tournament.maxKills}</span>
                    </div>
                  )}
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t('prizePool', 'Prize Pool')}:</span>
                    <span className="active-match-setting-value prize">{tournament.prizePool} {t('pts', 'pts')}</span>
                  </div>
                  {tournament.tournamentType === 'giveaway' && (
                    <div className="active-match-setting-item">
                      <span className="active-match-setting-label">{t('type', 'Type')}:</span>
                      <span className="active-match-setting-value giveaway">{t('giveaway', '🎁 GIVEAWAY')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Join Room Button */}
              {tournament.roomUrl && (
                <div className="active-match-join-section">
                  <button
                    onClick={() => window.open(tournament.roomUrl, '_blank')}
                    className="active-match-join-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t('joinRoom', 'Join Room')}
                  </button>
                </div>
              )}

              {/* Team Members / Placements */}
              <div className="active-match-team-section">
                <div className="active-match-team-header">
                  <h4 className="active-match-team-title">
                    {(isBRTeamGame || isMpTeamGame) ? t('teamPlacements', '👥 Team Placements') : t('participants', '👥 Participants')}
                  </h4>
                  {(isBRTeamGame || isMpTeamGame) && (
                    <button
                      onClick={() => setShowTeamPlacements(!showTeamPlacements)}
                      className="active-match-toggle-teams-btn"
                    >
                      {showTeamPlacements ? t('hideTeams', 'Hide') : t('showTeams', 'Show')}
                      {showTeamPlacements ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  )}
                </div>

                {/* BR Team Game - Show team placements */}
                {isBRTeamGame && showTeamPlacements && (
                  <div className="active-match-br-teams">
                    {getBRTeams().map(team => (
                      <div key={team.teamNumber} className="active-match-br-team">
                        <div className="active-match-br-team-header">
                          <span className="active-match-br-team-badge">
                            {tournament.multiplayerType === 'duo' ? '👥' : '🎯'} {t('team', 'Team')} {team.teamNumber}
                          </span>
                          <span className="active-match-br-team-count">{team.members.length}/{team.maxSize}</span>
                        </div>
                        <div className="active-match-br-team-members">
                          {team.members.map((member, idx) => (
                            <span
                              key={idx}
                              className={`active-match-br-member ${member.id === user._id ? 'self' : ''} ${member.isFiller ? 'filler' : ''}`}
                            >
                              {member.isFiller && '🤖 '}
                              {cleanAdminName(member.name)}
                              {member.id === user._id && ` (${t('you', 'You')})`}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* MP Team Game - Show team 1 vs team 2 */}
                {isMpTeamGame && showTeamPlacements && (
                  <div className="active-match-mp-teams">
                    <div className="active-match-mp-team team1">
                      <div className="active-match-mp-team-header">{t('team', 'Team')} 1</div>
                      {getMPTeams().team1.map((member, idx) => (
                        <span
                          key={idx}
                          className={`active-match-mp-member ${member.id === user._id ? 'self' : ''}`}
                        >
                          {cleanAdminName(member.name)}
                          {member.id === user._id && ` (${t('you', 'You')})`}
                        </span>
                      ))}
                    </div>
                    <div className="active-match-mp-vs">VS</div>
                    <div className="active-match-mp-team team2">
                      <div className="active-match-mp-team-header">{t('team', 'Team')} 2</div>
                      {getMPTeams().team2.map((member, idx) => (
                        <span
                          key={idx}
                          className={`active-match-mp-member ${member.id === user._id ? 'self' : ''}`}
                        >
                          {cleanAdminName(member.name)}
                          {member.id === user._id && ` (${t('you', 'You')})`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Simple list for solo/FFA games OR when teams hidden */}
                {(!isBRTeamGame && !isMpTeamGame) || !showTeamPlacements ? (
                  <div className="active-match-team-list">
                    {tournament.participants.map(participant => (
                      <div key={participant._id} className="active-match-team-member">
                        <span className={`active-match-team-indicator ${
                          participant._id === user._id ? 'self' : 'other'
                        }`}></span>
                        <span className={`active-match-team-name ${
                          participant._id === user._id ? 'self' : 'other'
                        }`}>
                          {cleanAdminName(participant.inGameName || participant.username || 'Unknown')}
                          {participant._id === user._id && ` (${t('you', 'You')})`}
                        </span>
                      </div>
                    ))}
                    {/* Show fillers */}
                    {(tournament.fillers || []).map((filler, idx) => (
                      <div key={`filler-${idx}`} className="active-match-team-member filler">
                        <span className="active-match-team-indicator filler"></span>
                        <span className="active-match-team-name filler">
                          🤖 {filler.name}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Main Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`active-match-main-button ${isCountdown ? 'countdown' : 'active'}`}
        >
          {isCountdown ? (
            <>
              <Timer className="w-4 h-4" />
              {countdown && countdown !== '0:00' ? countdown : t('standby', 'STANDBY')}
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              {t('matchLive', 'MATCH LIVE')}
            </>
          )}
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>
    </>
  );
};

const EmptyState = ({ icon, title, subtitle }) => (
  <div className="tournament-empty-state">
    <div className="tournament-empty-icon">{icon}</div>
    <p className="tournament-empty-title">{title}</p>
    <p className="tournament-empty-subtitle">{subtitle}</p>
  </div>
);

// Cancel Tournament Modal
const CancelTournamentModal = ({ tournament, reason, setReason, onConfirm, onCancel, loading, cleanAdminName, t }) => {
  if (!tournament) return null;

  const isGiveaway = tournament.tournamentType === 'giveaway';
  const participantCount = tournament.participants?.length || 0;

  return (
    <div className="cancel-tournament-modal-overlay">
      <div className="cancel-tournament-modal">
        <div className="cancel-tournament-modal-header">
          <div className="cancel-tournament-modal-icon">
            <XCircle className="w-6 h-6 text-white" />
          </div>
          <h3>{t('cancelTournament', 'Cancel Tournament')}</h3>
        </div>
        
        <div className="cancel-tournament-info">
          <p className="tournament-title">{cleanAdminName(tournament.creator?.inGameName || tournament.creator?.username || 'Unknown')}'s {tournament.gameMode} {t('tournament', 'Tournament')}</p>
          <p className="tournament-details">{t('status', 'Status')}: {tournament.status}</p>
          <p className="tournament-details">{t('participants', 'Participants')}: {participantCount}/{tournament.maxPlayers}</p>
          <p className="tournament-details">{t('prizePool', 'Prize Pool')}: {tournament.prizePool} {t('points', 'points')}</p>
          {isGiveaway && <p className="giveaway-indicator">{t('giveawayIndicator', '🎁 Giveaway Tournament')}</p>}
        </div>

        <div className="cancel-refund-warning">
          <p className="warning-title">{t('refundInformation', '⚠️ Refund Information:')}:</p>
          {isGiveaway ? (
            <p>
              {t('creatorWillReceive', 'The creator will receive')} {tournament.giveawayData?.totalCreatorCost || tournament.prizePool} {t('pointsBack', 'points back')}.
            </p>
          ) : (
            <p>
              {t('eachParticipantWillReceive', 'Each participant will receive their')} {tournament.entryFee} {t('entryFeeBack', 'entry fee back')}.
              {t('totalRefund', 'Total refund')}: {tournament.entryFee * participantCount} {t('points', 'points')}.
            </p>
          )}
        </div>

        <div className="cancel-reason-field">
          <label className="cancel-reason-label">
            {t('cancellationReason', 'Cancellation Reason')}
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('enterReasonForCancelling', 'Enter reason for cancelling this tournament...')}
            className="cancel-reason-textarea"
            rows={3}
          />
        </div>

        <div className="cancel-final-warning">
          <p>
            {t('thisActionCannotBeUndone', '⚠️ This action cannot be undone. All participants will be refunded and the tournament will be permanently cancelled.')}
          </p>
        </div>
        
        <div className="cancel-actions">
          <button
            onClick={onConfirm}
            disabled={loading}
            className="cancel-btn cancel-btn-confirm"
          >
            {loading ? t('cancelling', 'Cancelling...') : t('confirmCancellation', 'Confirm Cancellation')}
          </button>
          <button
            onClick={onCancel}
            disabled={loading}
            className="cancel-btn cancel-btn-keep"
          >
            {t('keepTournament', 'Keep Tournament')}
          </button>
        </div>
      </div>
    </div>
  );
};

// UPDATED: WaitingTournamentCard with VOTE EARLY START functionality
const WaitingTournamentCard = ({
  tournament,
  user,
  onJoin,
  onLeave,
  loading,
  additionalCount = 0,
  isExpanded,
  onToggleExpanded,
  isAdditional = false,
  getModeDisplay,
  getCustomModeDisplay,
  getTitleDisplay,
  getCustomModeWarning,
  getTimeSince,
  cleanAdminName,
  onTournamentUpdate,
  onUserUpdate,
  onOpenKickModal,
  onOpenBRViewModal,
  canKickFromTournament,
  t
}) => {
  const [voteLoading, setVoteLoading] = useState(false);
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const isParticipant = tournament.participants.some(p => p._id === user._id);
  const isGiveaway = tournament.tournamentType === 'giveaway';

  // Stake level tiers based on total prize
  const totalPrize = tournament.prizePool || 0;
  const stakeLevel = totalPrize >= 100000 ? 'legendary' :
                     totalPrize >= 50000 ? 'veryHigh' :
                     totalPrize >= 20000 ? 'high' : null;
  const isHighPrize = stakeLevel !== null; // For backward compatibility
  const canKick = canKickFromTournament(tournament, user);

  // Participant count (fillers removed from system)
  const realParticipants = tournament.participants || [];
  const actualParticipantCount = tournament.actualParticipantCount || realParticipants.length;

  const canJoin = !isParticipant && actualParticipantCount < tournament.maxPlayers;
  const canLeave = isParticipant;

  // Early start vote data from tournament
  const earlyStartVotes = tournament.earlyStartVotes || [];
  const requiredVotes = realParticipants.length;
  const hasUserVoted = earlyStartVotes.some(v => (v._id || v) === user._id);
  const voteCount = earlyStartVotes.length;
  const remainingVotes = requiredVotes - voteCount;

  // Calculate early prize pool if started now
  const calculateEarlyPrize = () => {
    if (isGiveaway) return { earlyPrize: tournament.prizePool, reduction: 0 };
    const currentEntryTotal = actualParticipantCount * tournament.entryFee;
    const commissionRate = 0.10; // 10% commission
    const earlyPrize = Math.floor(currentEntryTotal * (1 - commissionRate));
    const reduction = tournament.prizePool - earlyPrize;
    return { earlyPrize, reduction };
  };

  const prizeInfo = calculateEarlyPrize();
  const isNotFull = actualParticipantCount < tournament.maxPlayers;

  // Handle vote for early start
  const handleVote = async () => {
    if (voteLoading) return;
    setVoteLoading(true);

    try {
      const response = await axios.post(`/api/tournaments/${tournament._id}/vote-early-start`);

      if (response.data.voteStatus?.tournamentStarted) {
        toast.success(t('allVotedTournamentStarted', 'All players voted! Tournament started and ready for approval.'), {
          duration: 5000,
          style: { background: '#1f2937', color: '#10b981', border: '1px solid #059669' }
        });
      } else if (response.data.voteStatus?.awaitingCreatorStart) {
        // Giveaway match - all voted but waiting for creator to start
        toast.success(t('allVotedAwaitingCreator', 'All players voted! Waiting for the creator to start the match.'), {
          duration: 5000,
          style: { background: '#1f2937', color: '#fbbf24', border: '1px solid #f59e0b' }
        });
      } else {
        toast.success(t('voteRecorded', 'Vote recorded! ({current}/{total})').replace('{current}', response.data.voteStatus.totalVotes).replace('{total}', response.data.voteStatus.requiredVotes), {
          duration: 3000,
          style: { background: '#1f2937', color: '#60a5fa', border: '1px solid #3b82f6' }
        });
      }
      setShowVoteModal(false);
      onTournamentUpdate();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToVote', 'Failed to record vote');
      toast.error(message, {
        duration: 4000,
        style: { background: '#1f2937', color: '#fca5a5', border: '1px solid #ef4444' }
      });
    } finally {
      setVoteLoading(false);
    }
  };

  // Handle unvote
  const handleUnvote = async () => {
    if (voteLoading) return;
    setVoteLoading(true);

    try {
      await axios.post(`/api/tournaments/${tournament._id}/unvote-early-start`);
      toast.success(t('voteRemoved', 'Vote removed'), {
        duration: 2000,
        style: { background: '#1f2937', color: '#fbbf24', border: '1px solid #f59e0b' }
      });
      setShowVoteModal(false);
      onTournamentUpdate();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToRemoveVote', 'Failed to remove vote');
      toast.error(message, {
        duration: 4000,
        style: { background: '#1f2937', color: '#fca5a5', border: '1px solid #ef4444' }
      });
    } finally {
      setVoteLoading(false);
    }
  };

  // Check if user can vote (participant in waiting tournament with minimum players)
  const canVote = tournament.status === 'waiting' &&
                  isParticipant &&
                  realParticipants.length >= 2 &&
                  actualParticipantCount >= 2;

  // Giveaway creator controls - can control even if not participating
  const isCreator = tournament.creator._id === user._id;
  const isGiveawayCreator = isGiveaway && isCreator;
  const isGiveawayAdmin = isGiveaway && ['manager', 'main_admin'].includes(user?.role);
  const canGiveawayCreatorControl = (isGiveawayCreator || isGiveawayAdmin) &&
                                    tournament.status === 'waiting' &&
                                    actualParticipantCount >= 2;

  // Check if creator is a spectator (not playing but in the game)
  const creatorParticipation = tournament.creatorParticipation || (tournament.creatorJoins === false ? 'sponsor' : 'play');
  const isCreatorSpectator = isGiveaway && creatorParticipation === 'spectate';
  const isCreatorSponsorOnly = isGiveaway && creatorParticipation === 'sponsor';

  // Giveaway creator can always abort/leave their own tournament (even as spectator/sponsor)
  // Note: WaitingTournamentCard handles 'waiting' status tournaments
  const canCreatorAbort = isCreator && isGiveaway && tournament.status === 'waiting';
  
  // Manager and Main Admin can abort any waiting tournament
  const canAdminAbort = ['manager', 'main_admin'].includes(user?.role) && tournament.status === 'waiting';

  // Handle giveaway creator instant start
  const handleGiveawayInstantStart = async () => {
    if (voteLoading) return;
    setVoteLoading(true);

    try {
      const response = await axios.post(`/api/tournaments/${tournament._id}/start-now`);
      toast.success(t('giveawayStartedInstantly', 'Giveaway started! Ready for admin approval.'), {
        duration: 5000,
        style: { background: '#1f2937', color: '#fbbf24', border: '1px solid #f59e0b' }
      });
      onTournamentUpdate();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToStartGiveaway', 'Failed to start giveaway');
      toast.error(message, {
        duration: 4000,
        style: { background: '#1f2937', color: '#fca5a5', border: '1px solid #ef4444' }
      });
    } finally {
      setVoteLoading(false);
    }
  };

  // Handle giveaway creator initiate vote
  const handleGiveawayInitiateVote = async () => {
    if (voteLoading) return;
    setVoteLoading(true);

    try {
      await axios.post(`/api/tournaments/${tournament._id}/giveaway-initiate-vote`);
      toast.success(t('voteRequestSent', 'Vote request sent to all participants!'), {
        duration: 3000,
        style: { background: '#1f2937', color: '#a78bfa', border: '1px solid #8b5cf6' }
      });
      setShowVoteModal(false);
      onTournamentUpdate();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToInitiateVote', 'Failed to initiate vote');
      toast.error(message, {
        duration: 4000,
        style: { background: '#1f2937', color: '#fca5a5', border: '1px solid #ef4444' }
      });
    } finally {
      setVoteLoading(false);
    }
  };

  const voteInitiated = tournament.giveawayData?.voteInitiated;

  const getCardClasses = () => {
    const baseClasses = ['tournament-card-compact', 'tournament-card-base'];
    
    if (isGiveaway) {
      baseClasses.push('tournament-card-giveaway', 'gold-glow');
    } else if (isHighPrize) {
      baseClasses.push('tournament-card-high-prize');
    } else {
      baseClasses.push('tournament-card-standard');
    }
    
    return baseClasses.join(' ');
  };

  // Check if this is a multiplayer team game
  const teamGameModes = ['teamDeathmatch', 'domination', 'searchAndDestroy', 'hardpoint',
                         'killConfirmed', 'captureTheFlag', 'frontline', 'cranked'];
  const isMpTeamGame = teamGameModes.includes(tournament.gameMode) && tournament.multiplayerType === 'team';

  // Check if this is a BR Duo/Squad tournament
  const isBRDuoOrSquad = tournament.gameMode === 'battleRoyale' &&
                          ['duo', 'squad'].includes(tournament.multiplayerType);

  // Helper function to get user's MP team assignment
  const getUserMpTeam = (userId) => {
    if (!isMpTeamGame || !tournament.mpTeamAssignments) return null;
    const assignment = tournament.mpTeamAssignments.find(
      a => (a.userId?._id || a.userId)?.toString() === userId?.toString()
    );
    return assignment?.teamNumber || null;
  };

  // Helper function to get user's BR team assignment
  const getUserBRTeam = (userId) => {
    if (!isBRDuoOrSquad || !tournament.brTeamAssignments) return null;
    const assignment = tournament.brTeamAssignments.find(
      a => (a.userId?._id || a.userId)?.toString() === userId?.toString()
    );
    return assignment?.teamNumber || null;
  };

  // Get BR team info for a user
  const getBRTeamInfo = (userId) => {
    if (!isBRDuoOrSquad || !tournament.brTeamAssignments) return null;
    const assignment = tournament.brTeamAssignments.find(
      a => (a.userId?._id || a.userId)?.toString() === userId?.toString()
    );
    return assignment || null;
  };

  // Calculate BR teams
  const getBRTeams = () => {
    if (!isBRDuoOrSquad) return null;

    const maxTeamSize = tournament.multiplayerType === 'duo' ? 2 : 4;
    const teamCount = Math.ceil(tournament.maxPlayers / maxTeamSize);
    const teams = [];

    for (let i = 0; i < teamCount; i++) {
      const teamNumber = i + 1;
      const teamMembers = (tournament.brTeamAssignments || [])
        .filter(a => a.teamNumber === teamNumber)
        .map(a => ({
          _id: (a.userId?._id || a.userId)?.toString(),
          name: a.inGameName || a.username || 'Unknown',
          preference: a.preference,
          isFiller: a.isFiller
        }));

      teams.push({
        teamNumber,
        members: teamMembers,
        spotsRemaining: maxTeamSize - teamMembers.length
      });
    }

    return teams;
  };

  // Helper function to get all participants including fillers
  const getAllParticipants = () => {
    return tournament.participants.map(p => ({
      _id: p._id,
      type: 'real',
      name: cleanAdminName(p.inGameName || p.username || 'Unknown'),
      isCurrentUser: p._id === user._id,
      canBeKicked: canKick && p._id !== user._id,
      mpTeam: getUserMpTeam(p._id)
    }));
  };

  // Get participants grouped by team for MP team games
  const getParticipantsByTeam = () => {
    if (!isMpTeamGame) return null;
    const all = getAllParticipants();
    return {
      team1: all.filter(p => p.mpTeam === 1),
      team2: all.filter(p => p.mpTeam === 2),
      unassigned: all.filter(p => !p.mpTeam)
    };
  };

  const allParticipants = getAllParticipants();

  // Handle kick using parent modal
  const handleKickClick = (participant) => {
    if (onOpenKickModal) {
      onOpenKickModal(participant, tournament);
    }
  };

  return (
    <div className={getCardClasses()}>
      {/* Giveaway Badge - Bottom right */}
      {isGiveaway && (
        <div className="tournament-giveaway-badge">
          <span>🎁</span>
          <span>{t('giveaway', 'GIVEAWAY')}</span>
        </div>
      )}

      {/* Stake Level Badge - Bottom right (when not giveaway) */}
      {stakeLevel && !isGiveaway && (
        <div className={`tournament-stakes-badge tournament-stakes-${stakeLevel}`}>
          <span>{stakeLevel === 'legendary' ? '👑' : stakeLevel === 'veryHigh' ? '💎' : '🔥'}</span>
          <span>
            {stakeLevel === 'legendary' ? t('legendaryStakes', 'LEGENDARY STAKES') :
             stakeLevel === 'veryHigh' ? t('veryHighStakes', 'VERY HIGH STAKES') :
             t('highStakes', 'HIGH STAKES')}
          </span>
        </div>
      )}

      {/* Border decorations */}
      {isGiveaway && (
        <>
          <div className="tournament-overlay-giveaway"></div>
          <div className="tournament-border-glow-top"></div>
          <div className="tournament-border-glow-top-secondary"></div>
          <div className="tournament-border-glow-bottom"></div>
          <div className="tournament-border-glow-bottom-secondary"></div>
          <div className="tournament-border-glow-left"></div>
          <div className="tournament-border-glow-right"></div>
        </>
      )}
      {stakeLevel && !isGiveaway && (
        <>
          <div className={`tournament-border-stakes-top tournament-border-stakes-${stakeLevel}`}></div>
          <div className={`tournament-border-stakes-bottom tournament-border-stakes-${stakeLevel}`}></div>
        </>
      )}

      <div className="tournament-card-compact-main">
        <div className="tournament-card-compact-left">
          <div className="tournament-card-compact-title" style={{
            color: isGiveaway ? '#fef3c7' :
                   stakeLevel === 'legendary' ? '#fef08a' :
                   stakeLevel === 'veryHigh' ? '#c4b5fd' :
                   stakeLevel === 'high' ? '#fecaca' : 'white'
          }}>
            {getTitleDisplay(tournament)}
            {getCustomModeWarning(tournament) && (
              <span className="hidden sm:inline text-red-400 font-bold ml-2 text-xs">
                ⚠️{getCustomModeWarning(tournament)}
              </span>
            )}
          </div>

          {/* NEW: Giveaway Info Row */}
          {isGiveaway && tournament.giveawayData?.openingDate && (
            <div className="giveaway-info-row">
              <span className="giveaway-opening-date">
                📅 {t("opens", "Opens")}: {new Date(tournament.giveawayData.openingDate).toLocaleString()}
              </span>
              {tournament.giveawayData?.infoUrl && (
                <a
                  className="giveaway-info-link-btn"
                  href={tournament.giveawayData.infoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!window.confirm(t("visitUrlConfirm", "Are you sure you want to visit this external URL?"))) {
                      e.preventDefault();
                    }
                  }}
                >
                  🔗 {t("info", "Info")}
                </a>
              )}
            </div>
          )}
          
          <div className="tournament-card-compact-details">
            <span className="text-red-400">⚔️{getModeDisplay(tournament)}</span>
            <span className="text-blue-400">📍{tournament.map}</span>
            <span className="text-yellow-400">👥{actualParticipantCount}/{tournament.maxPlayers}</span>
            {['teamDeathmatch', 'domination', 'hardpoint', 'killConfirmed', 'captureTheFlag', 'frontline', 'freeForAll'].includes(tournament.gameMode) && tournament.maxKills && (
              <span className="text-pink-400">💀{tournament.maxKills}K</span>
            )}
            <span className={isGiveaway ? 'text-green-400 font-bold' : 'text-yellow-400'}>
              💰{isGiveaway ? t('free', 'FREE') : tournament.entryFee}
            </span>
            <span className={`font-bold ${isGiveaway ? 'text-yellow-400 animate-pulse' : isHighPrize ? 'text-red-400' : 'text-green-400'}`}>
              🏆{tournament.prizePool}
            </span>
            <span className="tournament-card-compact-time">⏰{getTimeSince(tournament.createdAt)}</span>
          </div>
        </div>

        <div className="tournament-card-compact-right">
          <div className="tournament-card-compact-actions">
            <div className={`tournament-status-base ${
              isGiveaway ? 'tournament-status-lobby-giveaway' :
              isHighPrize ? 'tournament-status-lobby-high-prize' :
              'tournament-status-lobby'
            }`}>
              {t('lobby', 'LOBBY')}
            </div>
            
            {/* VOTE EARLY START Button - For all participants */}
            {canVote && !isGiveawayCreator && (
              <button
                onClick={() => setShowVoteModal(true)}
                disabled={voteLoading}
                className={`tournament-btn-base tournament-btn-vote ${
                  hasUserVoted ? 'tournament-btn-voted' :
                  isGiveaway ? 'tournament-btn-vote-giveaway' :
                  isHighPrize ? 'tournament-btn-vote-high-prize' : ''
                }`}
                title={hasUserVoted
                  ? t('clickToManageVote', 'Click to manage your vote')
                  : t('voteToStartEarly', 'Vote to start tournament early')}
              >
                {voteLoading ? '...' : (
                  <>
                    {hasUserVoted ? '✓' : '🗳️'}
                    <span className="vote-count">{voteCount}/{requiredVotes}</span>
                  </>
                )}
              </button>
            )}

            {/* GIVEAWAY CREATOR CONTROLS - Instant Start & Vote Options */}
            {canGiveawayCreatorControl && (
              <>
                {/* Instant Start Button */}
                <button
                  onClick={handleGiveawayInstantStart}
                  disabled={voteLoading}
                  className="tournament-btn-base tournament-btn-giveaway-start"
                  title={t('startGiveawayNow', 'Start giveaway immediately')}
                >
                  {voteLoading ? '...' : (
                    <>
                      ⚡ {t('start', 'START')}
                    </>
                  )}
                </button>

                {/* Initiate Vote Button */}
                <button
                  onClick={() => setShowVoteModal(true)}
                  disabled={voteLoading || voteInitiated}
                  className={`tournament-btn-base tournament-btn-giveaway-vote ${voteInitiated ? 'vote-active' : ''}`}
                  title={voteInitiated
                    ? t('voteInProgress', 'Vote in progress ({current}/{total})').replace('{current}', voteCount).replace('{total}', requiredVotes)
                    : t('askPlayersToVote', 'Ask players to vote for early start')}
                >
                  {voteLoading ? '...' : (
                    <>
                      🗳️ {voteInitiated ? `${voteCount}/${requiredVotes}` : t('vote', 'VOTE')}
                    </>
                  )}
                </button>
              </>
            )}

            {/* Vote indicator for giveaway participants when vote is initiated */}
            {isGiveaway && !isGiveawayCreator && voteInitiated && canVote && (
              <button
                onClick={() => setShowVoteModal(true)}
                disabled={voteLoading}
                className={`tournament-btn-base tournament-btn-vote tournament-btn-vote-giveaway ${hasUserVoted ? 'tournament-btn-voted' : 'vote-requested'}`}
                title={hasUserVoted
                  ? t('youVoted', 'You voted for early start')
                  : t('creatorRequestsVote', 'Creator requests your vote!')}
              >
                {voteLoading ? '...' : (
                  <>
                    {hasUserVoted ? '✓' : '❗'}
                    <span className="vote-count">{voteCount}/{requiredVotes}</span>
                  </>
                )}
              </button>
            )}

            {canJoin && (
              <button onClick={() => onJoin(tournament)} disabled={loading}
                className={`tournament-btn-base tournament-btn-deploy ${
                  isGiveaway ? 'tournament-btn-deploy-giveaway' : 
                  isHighPrize ? 'tournament-btn-deploy-high-prize' : ''
                }`}>
                {loading ? '...' : t('join', 'JOIN')}
              </button>
            )}
            
            {(canLeave || canCreatorAbort || canAdminAbort) && (
              <button onClick={() => onLeave(tournament._id)} disabled={loading}
                className="tournament-btn-base tournament-btn-abort">
                {loading ? '...' : t('abort', 'ABORT')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bottom section with team */}
      <div className="tournament-card-compact-bottom">
        <div className="tournament-card-compact-team">
          {/* For MP Team Games - Show Team 1 vs Team 2 */}
          {isMpTeamGame ? (
            <>
              <div className="mp-teams-display">
                {/* Team 1 */}
                <div className="mp-team-section team-1">
                  <span className="mp-team-label" style={{ color: '#60a5fa' }}>
                    {t('team', 'Team')} 1
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {getParticipantsByTeam()?.team1.map(participant => (
                      <span
                        key={participant._id}
                        className={`tournament-participant-badge ${
                          participant.isCurrentUser ? 'tournament-participant-self' : 'tournament-participant-other'
                        } ${participant.canBeKicked ? 'tournament-participant-kickable' : ''}`}
                        onClick={() => participant.canBeKicked && handleKickClick(participant)}
                        title={participant.canBeKicked ? t('clickToKick', 'Click to kick {name}').replace('{name}', participant.name) : ''}
                      >
                        👤{participant.name}
                      </span>
                    ))}
                    {(() => {
                      const teamSettings = tournament.mpTeamSettings?.team1;
                      const teammates = teamSettings?.teammates || [];
                      const filledCount = getParticipantsByTeam()?.team1.length || 0;
                      const teamSize = Math.ceil(tournament.maxPlayers / 2);
                      const emptySlots = teamSize - filledCount;
                      const hasCaptain = !!teamSettings?.captainId;
                      const isSpecificMode = teamSettings?.preference === 'specific';
                      // Check if current user is on this team
                      const isUserOnThisTeam = getUserMpTeam(user?._id) === 1;

                      return Array.from({ length: emptySlots }).map((_, idx) => {
                        const slot = teammates[idx];
                        // Slot is locked if:
                        // 1. Explicitly marked as locked (isLocked: true)
                        // 2. Team has captain with 'specific' preference but no slot defined for this index
                        // 3. Team has captain with 'specific' preference and slot has no name and not random
                        const isExplicitlyLocked = slot?.isLocked === true;
                        const isImplicitlyLocked = hasCaptain && isSpecificMode && (!slot || (!slot.name && !slot.isRandom));
                        const isLocked = isExplicitlyLocked || isImplicitlyLocked;
                        const isSpecific = slot?.name && !slot?.isRandom && !slot?.isLocked;
                        // Only show player name to teammates
                        const showName = isSpecific && isUserOnThisTeam;

                        return (
                          <span
                            key={`empty-t1-${idx}`}
                            className={`tournament-participant-empty ${isLocked ? 'slot-locked' : ''} ${isSpecific ? 'slot-specific' : ''}`}
                            title={isLocked ? t('lockedSlot', 'Locked Slot') : showName ? `${t('waitingFor', 'Waiting for')}: ${slot.name}` : isSpecific ? t('reservedSlot', 'Reserved Slot') : t('openSlot', 'Open Slot')}
                          >
                            {isLocked ? '🔒' : showName ? `⏳${slot.name.substring(0, 6)}` : isSpecific ? '⏳' : '👤---'}
                          </span>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* VS Divider */}
                <div className="mp-vs-divider">
                  <span style={{ color: '#f97316', fontWeight: 'bold' }}>VS</span>
                </div>

                {/* Team 2 */}
                <div className="mp-team-section team-2">
                  <span className="mp-team-label" style={{ color: '#f87171' }}>
                    {t('team', 'Team')} 2
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {getParticipantsByTeam()?.team2.map(participant => (
                      <span
                        key={participant._id}
                        className={`tournament-participant-badge ${
                          participant.isCurrentUser ? 'tournament-participant-self' : 'tournament-participant-other'
                        } ${participant.canBeKicked ? 'tournament-participant-kickable' : ''}`}
                        onClick={() => participant.canBeKicked && handleKickClick(participant)}
                        title={participant.canBeKicked ? t('clickToKick', 'Click to kick {name}').replace('{name}', participant.name) : ''}
                      >
                        👤{participant.name}
                      </span>
                    ))}
                    {(() => {
                      const teamSettings = tournament.mpTeamSettings?.team2;
                      const teammates = teamSettings?.teammates || [];
                      const filledCount = getParticipantsByTeam()?.team2.length || 0;
                      const teamSize = Math.ceil(tournament.maxPlayers / 2);
                      const emptySlots = teamSize - filledCount;
                      const hasCaptain = !!teamSettings?.captainId;
                      const isSpecificMode = teamSettings?.preference === 'specific';
                      // Check if current user is on this team
                      const isUserOnThisTeam = getUserMpTeam(user?._id) === 2;

                      return Array.from({ length: emptySlots }).map((_, idx) => {
                        const slot = teammates[idx];
                        // Slot is locked if:
                        // 1. Explicitly marked as locked (isLocked: true)
                        // 2. Team has captain with 'specific' preference but no slot defined
                        // 3. Team has captain with 'specific' preference and slot has no name and not random
                        const isExplicitlyLocked = slot?.isLocked === true;
                        const isImplicitlyLocked = hasCaptain && isSpecificMode && (!slot || (!slot.name && !slot.isRandom));
                        const isLocked = isExplicitlyLocked || isImplicitlyLocked;
                        const isSpecific = slot?.name && !slot?.isRandom && !slot?.isLocked;
                        // Only show player name to teammates
                        const showName = isSpecific && isUserOnThisTeam;

                        return (
                          <span
                            key={`empty-t2-${idx}`}
                            className={`tournament-participant-empty ${isLocked ? 'slot-locked' : ''} ${isSpecific ? 'slot-specific' : ''}`}
                            title={isLocked ? t('lockedSlot', 'Locked Slot') : showName ? `${t('waitingFor', 'Waiting for')}: ${slot.name}` : isSpecific ? t('reservedSlot', 'Reserved Slot') : t('openSlot', 'Open Slot')}
                          >
                            {isLocked ? '🔒' : showName ? `⏳${slot.name.substring(0, 6)}` : isSpecific ? '⏳' : '👤---'}
                          </span>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
              {/* Edit Team Button for MP */}
              {isParticipant && getUserMpTeam(user._id) && onOpenBRViewModal && (
                <div className="mp-view-team-btn-container">
                  <button
                    className="br-edit-team-btn"
                    onClick={() => onOpenBRViewModal(tournament)}
                  >
                    ✏️ {t('editTeam', 'Edit Team')}
                  </button>
                </div>
              )}
            </>
          ) : isBRDuoOrSquad ? (
            /* BR Duo/Squad - Show View Players Button */
            <div className="br-lobby-summary">
              <div className="br-lobby-info">
                <span className="br-lobby-count">
                  👥 {actualParticipantCount}/{tournament.maxPlayers} {t('players', 'Players')}
                </span>
                <span className="br-lobby-teams">
                  🎮 {getBRTeams()?.filter(team => team.members.length > 0).length || 0} {t('teamsFormed', 'Teams')}
                </span>
                {tournament.brTeamSettings?.mode && (
                  <span className={`br-mode-badge br-mode-${tournament.brTeamSettings.mode}`}>
                    {tournament.brTeamSettings.mode === 'solo' ? '👤 Solo' :
                     tournament.brTeamSettings.mode === 'random' ? '🎲 Random' : '👥 Squad'}
                  </span>
                )}
              </div>
              <div className="br-lobby-buttons">
                <button
                  className="br-view-players-btn"
                  onClick={() => setShowPlayersModal(true)}
                >
                  👁️ {t('viewPlayers', 'View Players')}
                </button>
                {isParticipant && getUserBRTeam(user._id) && onOpenBRViewModal && (
                  <button
                    className="br-edit-team-btn"
                    onClick={() => onOpenBRViewModal(tournament)}
                  >
                    ✏️ {t('editTeam', 'Edit Team')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            /* Regular/Solo tournaments - Show Participants Button */
            <div className="tournament-participants-summary">
              <button
                onClick={() => setShowPlayersModal(true)}
                className="show-participants-btn"
              >
                👥 {t('showParticipants', 'Show Participants')} ({actualParticipantCount}/{tournament.maxPlayers})
              </button>
              {isCreatorSpectator && (
                <span className="tournament-spectator-indicator">
                  👀 {t('spectatorMode', '+Spectator')}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="tournament-card-compact-info">
          {tournament.password && <span className="text-yellow-500 font-bold">{t('passwordProtected', '🔒PWD')}</span>}

          {additionalCount > 0 && (
            <button onClick={onToggleExpanded} className="tournament-card-compact-expand">
              {isExpanded ? '[-]' : `[+${additionalCount}]`}
            </button>
          )}
        </div>
      </div>

      {/* Vote Early Start Modal */}
      {showVoteModal && ReactDOM.createPortal(
        <div className="vote-modal-overlay" onClick={() => setShowVoteModal(false)}>
          <div className="vote-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vote-modal-header">
              <h3>🗳️ {t('voteEarlyStart', 'Vote to Start Early')}</h3>
              <button className="vote-modal-close" onClick={() => setShowVoteModal(false)}>✕</button>
            </div>

            <div className="vote-modal-content">
              {/* Prize Warning */}
              {isNotFull && prizeInfo.reduction > 0 && (
                <div className="vote-prize-warning">
                  <div className="vote-warning-icon">⚠️</div>
                  <div className="vote-warning-text">
                    <strong>{t('prizeReduction', 'Prize Reduction Warning')}</strong>
                    <p>{t('lobbyNotFull', 'Lobby is not full ({current}/{max} players)').replace('{current}', actualParticipantCount).replace('{max}', tournament.maxPlayers)}</p>
                    <div className="vote-prize-comparison">
                      <span className="vote-prize-original">
                        {t('currentPrize', 'Current')}: <strong>{tournament.prizePool}P</strong>
                      </span>
                      <span className="vote-prize-arrow">→</span>
                      <span className="vote-prize-early">
                        {t('earlyPrize', 'Early Start')}: <strong>{prizeInfo.earlyPrize}P</strong>
                      </span>
                      <span className="vote-prize-diff">
                        (-{prizeInfo.reduction}P)
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Vote Status */}
              <div className="vote-status-section">
                <div className="vote-progress">
                  <div className="vote-progress-bar">
                    <div
                      className="vote-progress-fill"
                      style={{ width: `${(voteCount / requiredVotes) * 100}%` }}
                    />
                  </div>
                  <span className="vote-progress-text">
                    {voteCount}/{requiredVotes} {t('votesNeeded', 'votes needed')}
                  </span>
                </div>

                {/* Voters List */}
                <div className="vote-voters-list">
                  <strong>{t('votedPlayers', 'Voted Players')}:</strong>
                  <div className="vote-voters">
                    {realParticipants.map(p => {
                      const hasVoted = earlyStartVotes.some(v => (v._id || v) === p._id);
                      return (
                        <span
                          key={p._id}
                          className={`vote-voter ${hasVoted ? 'voted' : 'pending'} ${p._id === user._id ? 'self' : ''}`}
                        >
                          {hasVoted ? '✓' : '○'} {cleanAdminName(p.inGameName || p.username)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="vote-modal-actions">
                {/* Giveaway Creator Actions */}
                {isGiveawayCreator ? (
                  <>
                    {/* Show Start Now button when all votes are in */}
                    {voteCount >= requiredVotes && requiredVotes > 0 ? (
                      <div className="vote-all-voted-giveaway">
                        <div className="vote-all-voted-message">
                          <span className="vote-all-voted-icon">✅</span>
                          <span>{t('allPlayersVoted', 'All players have voted!')}</span>
                        </div>
                        <button
                          className="vote-btn vote-btn-start-giveaway"
                          onClick={handleGiveawayInstantStart}
                          disabled={voteLoading}
                        >
                          {voteLoading ? '...' : `⚡ ${t('startMatchNow', 'Start Match Now')}`}
                        </button>
                      </div>
                    ) : !voteInitiated ? (
                      <button
                        className="vote-btn vote-btn-initiate"
                        onClick={handleGiveawayInitiateVote}
                        disabled={voteLoading}
                      >
                        {voteLoading ? '...' : t('requestVotes', 'Request All Players to Vote')}
                      </button>
                    ) : (
                      <div className="vote-initiated-status">
                        <span className="vote-initiated-icon">✅</span>
                        <span>{t('voteRequestSentWaiting', 'Vote request sent! Waiting for players...')}</span>
                      </div>
                    )}
                  </>
                ) : (
                  /* Regular Participant Actions */
                  <>
                    {hasUserVoted ? (
                      <button
                        className="vote-btn vote-btn-unvote"
                        onClick={handleUnvote}
                        disabled={voteLoading}
                      >
                        {voteLoading ? '...' : t('removeVote', 'Remove My Vote')}
                      </button>
                    ) : (
                      <button
                        className="vote-btn vote-btn-vote"
                        onClick={handleVote}
                        disabled={voteLoading}
                      >
                        {voteLoading ? '...' : t('voteToStart', 'Vote to Start Early')}
                      </button>
                    )}
                  </>
                )}
                <button
                  className="vote-btn vote-btn-cancel"
                  onClick={() => setShowVoteModal(false)}
                >
                  {t('close', 'Close')}
                </button>
              </div>

              <p className="vote-modal-note">
                {isGiveaway
                  ? t('voteNoteGiveaway', 'For giveaway matches, the creator must manually start the match after all votes are in.')
                  : t('voteNote', 'Tournament will start automatically when all real players vote.')}
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* BR Players Modal - Using Portal to render at document body level */}
      {showPlayersModal && isBRDuoOrSquad && ReactDOM.createPortal(
        <div className="br-players-modal-overlay" onClick={() => setShowPlayersModal(false)}>
          <div className="br-players-modal" onClick={(e) => e.stopPropagation()}>
            <div className="br-players-modal-header">
              <h3>
                🎮 {tournament.multiplayerType === 'duo' ? t('duoTeams', 'Duo Teams') : t('squadTeams', 'Squad Teams')}
                <span className="br-players-modal-count">
                  ({actualParticipantCount}/{tournament.maxPlayers})
                </span>
              </h3>
              <button className="br-players-modal-close" onClick={() => setShowPlayersModal(false)}>
                ✕
              </button>
            </div>
            <div className="br-players-modal-content">
              <div className="br-players-modal-grid">
                {getBRTeams()?.map(team => (
                  <div key={team.teamNumber} className={`br-players-modal-team ${
                    team.members.some(m => m._id === user._id) ? 'br-team-mine' : ''
                  } ${team.members.length === 0 ? 'br-team-empty' : ''}`}>
                    <div className="br-players-modal-team-header">
                      <span className="br-team-num">T{String(team.teamNumber).padStart(2, '0')}</span>
                      <span className="br-team-member-count">
                        {team.members.length}/{tournament.multiplayerType === 'duo' ? 2 : 4}
                      </span>
                    </div>
                    <div className="br-players-modal-team-members">
                      {team.members.map(member => (
                        <div
                          key={member._id}
                          className={`br-player-item ${member._id === user._id ? 'self' : ''} ${member.isFiller ? 'filler' : ''}`}
                        >
                          <span className="br-player-icon">{member.isFiller ? '🤖' : '👤'}</span>
                          <span className="br-player-name">{cleanAdminName(member.name)}</span>
                          {member.preference === 'solo' && <span className="br-pref-badge solo">S</span>}
                          {member.preference === 'squad' && <span className="br-pref-badge squad">Q</span>}
                        </div>
                      ))}
                      {Array.from({ length: team.spotsRemaining }).map((_, idx) => (
                        <div key={`empty-${idx}`} className="br-player-item empty">
                          <span className="br-player-icon">👤</span>
                          <span className="br-player-name">---</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Regular Participants Modal - For Solo/FFA tournaments */}
      {showPlayersModal && !isBRDuoOrSquad && !isMpTeamGame && ReactDOM.createPortal(
        <div className="participants-modal-overlay" onClick={() => setShowPlayersModal(false)}>
          <div className="participants-modal" onClick={e => e.stopPropagation()}>
            <div className="participants-modal-header">
              <h3>👥 {t('participants', 'Participants')} ({actualParticipantCount}/{tournament.maxPlayers})</h3>
              <button
                className="participants-modal-close"
                onClick={() => setShowPlayersModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="participants-modal-content">
              <div className="participants-grid-layout" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px" }}>
                {allParticipants.map(participant => (
                  <span
                    key={participant._id}
                    className={`tournament-participant-badge ${
                      participant.type === 'filler' ? 'tournament-participant-filler' :
                      participant.isCurrentUser
                        ? 'tournament-participant-self'
                        : 'tournament-participant-other'
                    } ${participant.canBeKicked ? 'tournament-participant-kickable' : ''}`}
                    onClick={() => {
                      if (participant.canBeKicked) {
                        handleKickClick(participant);
                      }
                    }}
                    title={participant.canBeKicked ? t('clickToKick', 'Click to kick {name}').replace('{name}', participant.name) : ''}
                  >
                    {participant.type === 'filler' ? '🤖' : '👤'}
                    {participant.name}
                    {participant.isCurrentUser && ` (${t('you', 'You')})`}
                    {participant.type === 'filler' && participant.isMyFiller && (
                      <span className="text-xs opacity-75"> (Your Filler)</span>
                    )}
                  </span>
                ))}
              </div>
              {/* Empty slots */}
              <div className="participants-grid-layout mt-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px" }}>
                {Array.from({ length: tournament.maxPlayers - actualParticipantCount }).map((_, index) => (
                  <span key={index} className="tournament-participant-empty">👤---</span>
                ))}
              </div>
              {/* Spectator display for giveaway tournaments */}
              {isCreatorSpectator && (
                <div className="tournament-spectator-display mt-2">
                  <span className="tournament-spectator-label">👀 {t('spectator', 'SPECTATOR')}:</span>
                  <span className={`tournament-participant-badge tournament-participant-spectator ${isCreator ? 'tournament-participant-self' : ''}`}>
                    👀 {cleanAdminName(tournament.creator.inGameName || tournament.creator.username)}
                    {isCreator && <span className="text-xs opacity-75"> ({t('you', 'You')})</span>}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Enhanced ApprovalTournamentCard with wait time warnings, RTL support, FILLER SUPPORT, and TEAM DISPLAY
const ApprovalTournamentCard = ({ tournament, user, onCancel, getModeDisplay, getCustomModeDisplay, getTitleDisplay, getCustomModeWarning, getTimeSince, cleanAdminName, t }) => {
  const isGiveaway = tournament.tournamentType === 'giveaway';

  // Stake level tiers based on total prize
  const totalPrize = tournament.prizePool || 0;
  const stakeLevel = totalPrize >= 100000 ? 'legendary' :
                     totalPrize >= 50000 ? 'veryHigh' :
                     totalPrize >= 20000 ? 'high' : null;
  const isHighPrize = stakeLevel !== null;
  const waitWarning = getWaitTimeWarning(tournament.readyForApprovalAt, t);

  // Include fillers in participant count and display
  const realParticipants = tournament.participants || [];
  const fillers = tournament.fillers || [];
  const totalParticipants = realParticipants.length + fillers.length;
  const actualParticipantCount = tournament.actualParticipantCount || totalParticipants;

  // Check if this is a multiplayer team game
  const teamGameModes = ['teamDeathmatch', 'domination', 'searchAndDestroy', 'hardpoint',
                         'killConfirmed', 'captureTheFlag', 'frontline', 'cranked'];
  const isMpTeamGame = teamGameModes.includes(tournament.gameMode) && tournament.multiplayerType === 'team';

  // Check if this is a BR Duo/Squad tournament
  const isBRDuoOrSquad = tournament.gameMode === 'battleRoyale' &&
                          ['duo', 'squad'].includes(tournament.multiplayerType);

  // Helper function to get user's MP team assignment
  const getUserMpTeam = (userId) => {
    if (!isMpTeamGame || !tournament.mpTeamAssignments) return null;
    const assignment = tournament.mpTeamAssignments.find(
      a => (a.userId?._id || a.userId)?.toString() === userId?.toString()
    );
    return assignment?.teamNumber || null;
  };

  // Calculate BR teams
  const getBRTeams = () => {
    if (!isBRDuoOrSquad) return null;

    const maxTeamSize = tournament.multiplayerType === 'duo' ? 2 : 4;
    const teamCount = Math.ceil(tournament.maxPlayers / maxTeamSize);
    const teams = [];

    for (let i = 0; i < teamCount; i++) {
      const teamNumber = i + 1;
      const teamMembers = (tournament.brTeamAssignments || [])
        .filter(a => a.teamNumber === teamNumber)
        .map(a => ({
          _id: (a.userId?._id || a.userId)?.toString(),
          name: a.inGameName || a.username || 'Unknown',
          preference: a.preference,
          isFiller: a.isFiller
        }));

      teams.push({
        teamNumber,
        members: teamMembers,
        spotsRemaining: maxTeamSize - teamMembers.length
      });
    }

    return teams;
  };

  // Helper function to get all participants including fillers
  const getAllParticipants = () => {
    const realParticipants = tournament.participants.map(p => ({
      _id: p._id,
      type: 'real',
      name: cleanAdminName(p.inGameName || p.username || 'Unknown'),
      isCurrentUser: p._id === user._id,
      mpTeam: getUserMpTeam(p._id)
    }));

    const fillerParticipants = fillers.map(f => ({
      _id: f._id,
      type: 'filler',
      name: f.name,
      addedBy: f.addedBy,
      isCurrentUser: false,
      isMyFiller: f.addedBy === user._id,
      mpTeam: null
    }));

    return [...realParticipants, ...fillerParticipants];
  };

  // Get participants grouped by team for MP team games
  const getParticipantsByTeam = () => {
    if (!isMpTeamGame) return null;
    const all = getAllParticipants();
    return {
      team1: all.filter(p => p.mpTeam === 1),
      team2: all.filter(p => p.mpTeam === 2),
      unassigned: all.filter(p => !p.mpTeam)
    };
  };

  const allParticipants = getAllParticipants();
  
  const getCardClasses = () => {
    const baseClasses = ['tournament-card-compact', 'tournament-card-base'];
    
    if (isGiveaway) {
      baseClasses.push('tournament-card-giveaway', 'gold-glow');
    } else if (isHighPrize) {
      baseClasses.push('tournament-card-high-prize');
    } else {
      baseClasses.push('bg-orange-900/20');
    }
    
    return baseClasses.join(' ');
  };

  return (
    <div className={getCardClasses()}>
      {/* Giveaway Badge - Bottom right */}
      {isGiveaway && (
        <div className="tournament-giveaway-badge">
          <span>🎁</span>
          <span>{t('giveaway', 'GIVEAWAY')}</span>
        </div>
      )}
      
      <div className="tournament-card-compact-main">
        <div className="tournament-card-compact-left">
          <div className="tournament-card-compact-title" style={{ color: isGiveaway ? '#fef3c7' : isHighPrize ? '#fecaca' : 'white' }}>
            {getTitleDisplay(tournament)}
            {getCustomModeWarning(tournament) && (
              <span className="hidden sm:inline text-red-400 font-bold ml-2 text-xs">
                ⚠️{getCustomModeWarning(tournament)}
              </span>
            )}
          </div>
          
          <div className="tournament-card-compact-details">
            <span className="text-red-400">⚔️{getModeDisplay(tournament)}</span>
            <span className="text-blue-400">📍{tournament.map}</span>
            <span className="text-yellow-400">👥{actualParticipantCount}/{tournament.maxPlayers}</span>
            {fillers.length > 0 && (
              <span className="text-blue-400">🤖{fillers.length}</span>
            )}
            <span className={`font-bold ${isGiveaway ? 'text-yellow-400 animate-pulse' : isHighPrize ? 'text-red-400' : 'text-green-400'}`}>
              🏆{tournament.prizePool}
            </span>
            <span className="tournament-card-compact-time">⏰{getTimeSince(tournament.createdAt)}</span>
          </div>
          
          {/* Wait time warning */}
          {waitWarning.show && (
            <div className={`tournament-wait-time-warning ${waitWarning.critical ? 'tournament-wait-time-critical' : ''}`}>
              {waitWarning.text}
            </div>
          )}
        </div>
        
        <div className="tournament-card-compact-right">
          <div className="tournament-card-compact-actions">
            <div className={`tournament-status-base ${
              isGiveaway ? 'tournament-status-lobby-giveaway' :
              isHighPrize ? 'tournament-status-lobby-high-prize' :
              'tournament-status-deploying'
            }`}>
              {t('deploying', 'DEPLOYING')}
            </div>
            
            {onCancel && (
              <button
                onClick={() => onCancel(tournament)}
                className="tournament-btn-base tournament-btn-cancel"
              >
                <XCircle className="w-3 h-3" />
                {t('cancel', 'CANCEL')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bottom section with team */}
      <div className="tournament-card-compact-bottom">
        <div className="tournament-card-compact-team">
          {/* For MP Team Games - Show Team 1 vs Team 2 */}
          {isMpTeamGame ? (
            <>
              <div className="mp-teams-display">
                {/* Team 1 */}
                <div className="mp-team-section team-1">
                  <span className="mp-team-label" style={{ color: '#60a5fa' }}>
                    {t('team', 'Team')} 1
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {getParticipantsByTeam()?.team1.map(participant => (
                      <span
                        key={participant._id}
                        className={`tournament-participant-badge ${
                          participant.type === 'filler' ? 'tournament-participant-filler' :
                          participant.isCurrentUser ? 'tournament-participant-self' : 'tournament-participant-other'
                        }`}
                      >
                        {participant.type === 'filler' ? '🤖' : '👤'}{participant.name}
                      </span>
                    ))}
                    {Array.from({ length: Math.ceil(tournament.maxPlayers / 2) - (getParticipantsByTeam()?.team1.length || 0) }).map((_, idx) => (
                      <span key={`empty-t1-${idx}`} className="tournament-participant-empty">👤---</span>
                    ))}
                  </div>
                </div>

                {/* VS Divider */}
                <div className="mp-vs-divider">
                  <span style={{ color: '#f97316', fontWeight: 'bold' }}>VS</span>
                </div>

                {/* Team 2 */}
                <div className="mp-team-section team-2">
                  <span className="mp-team-label" style={{ color: '#f87171' }}>
                    {t('team', 'Team')} 2
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {getParticipantsByTeam()?.team2.map(participant => (
                      <span
                        key={participant._id}
                        className={`tournament-participant-badge ${
                          participant.type === 'filler' ? 'tournament-participant-filler' :
                          participant.isCurrentUser ? 'tournament-participant-self' : 'tournament-participant-other'
                        }`}
                      >
                        {participant.type === 'filler' ? '🤖' : '👤'}{participant.name}
                      </span>
                    ))}
                    {Array.from({ length: Math.ceil(tournament.maxPlayers / 2) - (getParticipantsByTeam()?.team2.length || 0) }).map((_, idx) => (
                      <span key={`empty-t2-${idx}`} className="tournament-participant-empty">👤---</span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : isBRDuoOrSquad ? (
            /* BR Duo/Squad - Show Teams Grid */
            <div className="br-teams-display">
              <div className="br-teams-header">
                <span className="br-teams-label">
                  🎮 {tournament.multiplayerType === 'duo' ? t('duoTeams', 'Duo Teams') : t('squadTeams', 'Squad Teams')}
                </span>
                {tournament.brTeamSettings?.mode && (
                  <span className={`br-mode-badge br-mode-${tournament.brTeamSettings.mode}`}>
                    {tournament.brTeamSettings.mode === 'solo' ? '👤 Solo' :
                     tournament.brTeamSettings.mode === 'random' ? '🎲 Random' : '👥 Squad'}
                  </span>
                )}
              </div>
              <div className="br-teams-grid-compact">
                {getBRTeams()?.filter(team => team.members.length > 0).map(team => (
                  <div key={team.teamNumber} className={`br-team-compact ${
                    team.members.some(m => m._id === user._id) ? 'br-team-mine' : ''
                  }`}>
                    <span className="br-team-num">T{String(team.teamNumber).padStart(2, '0')}</span>
                    <div className="br-team-members-compact">
                      {team.members.map(member => (
                        <span
                          key={member._id}
                          className={`br-member-badge ${member._id === user._id ? 'br-member-self' : ''}`}
                          title={member.name}
                        >
                          {member.isFiller ? '🤖' : '👤'}{member.name}
                        </span>
                      ))}
                      {Array.from({ length: team.spotsRemaining }).map((_, idx) => (
                        <span key={`empty-${idx}`} className="br-member-empty">---</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Default - Simple participant list */
            <>
              <span className="tournament-card-compact-team-label">{t('team', 'TEAM')}:</span>
              <div className="participants-grid-layout" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px" }}>
                {allParticipants.map(participant => (
                  <span key={participant._id}
                    className={`tournament-participant-badge ${
                      participant.type === 'filler' ? 'tournament-participant-filler' :
                      participant.isCurrentUser
                        ? 'tournament-participant-self'
                        : 'tournament-participant-other'
                    }`}>
                    {participant.type === 'filler' ? '🤖' : '👤'}
                    {participant.name}
                    {participant.type === 'filler' && participant.isMyFiller && (
                      <span className="text-xs opacity-75"> (Your Filler)</span>
                    )}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// OngoingTournamentCard with Access Control, similar layout changes, RTL support, FILLER SUPPORT, and TEAM DISPLAY
const OngoingTournamentCard = ({ tournament, user, countdown, onCopyMatchId, onTournamentUpdate, onCancel, canSeeRoomId, getModeDisplay, getCustomModeDisplay, getTitleDisplay, getCustomModeWarning, getTimeSince, cleanAdminName, t }) => {
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const isParticipant = tournament.participants.some(p => p._id === user._id);
  const isGiveaway = tournament.tournamentType === 'giveaway';

  // Stake level tiers based on total prize
  const totalPrize = tournament.prizePool || 0;
  const stakeLevel = totalPrize >= 100000 ? 'legendary' :
                     totalPrize >= 50000 ? 'veryHigh' :
                     totalPrize >= 20000 ? 'high' : null;
  const isHighPrize = stakeLevel !== null;

  // Include fillers in participant display
  const realParticipants = tournament.participants || [];
  const fillers = tournament.fillers || [];
  const totalParticipants = realParticipants.length + fillers.length;
  const actualParticipantCount = tournament.actualParticipantCount || totalParticipants;

  // Check if this is a multiplayer team game
  const teamGameModes = ['teamDeathmatch', 'domination', 'searchAndDestroy', 'hardpoint',
                         'killConfirmed', 'captureTheFlag', 'frontline', 'cranked'];
  const isMpTeamGame = teamGameModes.includes(tournament.gameMode) && tournament.multiplayerType === 'team';

  // Check if this is a BR Duo/Squad tournament
  const isBRDuoOrSquad = tournament.gameMode === 'battleRoyale' &&
                          ['duo', 'squad'].includes(tournament.multiplayerType);

  // Helper function to get user's MP team assignment
  const getUserMpTeam = (userId) => {
    if (!isMpTeamGame || !tournament.mpTeamAssignments) return null;
    const assignment = tournament.mpTeamAssignments.find(
      a => (a.userId?._id || a.userId)?.toString() === userId?.toString()
    );
    return assignment?.teamNumber || null;
  };

  // Calculate BR teams
  const getBRTeams = () => {
    if (!isBRDuoOrSquad) return null;

    const maxTeamSize = tournament.multiplayerType === 'duo' ? 2 : 4;
    const teamCount = Math.ceil(tournament.maxPlayers / maxTeamSize);
    const teams = [];

    for (let i = 0; i < teamCount; i++) {
      const teamNumber = i + 1;
      const teamMembers = (tournament.brTeamAssignments || [])
        .filter(a => a.teamNumber === teamNumber)
        .map(a => ({
          _id: (a.userId?._id || a.userId)?.toString(),
          name: a.inGameName || a.username || 'Unknown',
          preference: a.preference,
          isFiller: a.isFiller
        }));

      teams.push({
        teamNumber,
        members: teamMembers,
        spotsRemaining: maxTeamSize - teamMembers.length
      });
    }

    return teams;
  };

  // Helper function to get all participants including fillers
  const getAllParticipants = () => {
    const realParticipants = tournament.participants.map(p => ({
      _id: p._id,
      type: 'real',
      name: cleanAdminName(p.inGameName || p.username || 'Unknown'),
      isCurrentUser: p._id === user._id,
      mpTeam: getUserMpTeam(p._id)
    }));

    const fillerParticipants = fillers.map(f => ({
      _id: f._id,
      type: 'filler',
      name: f.name,
      addedBy: f.addedBy,
      isCurrentUser: false,
      isMyFiller: f.addedBy === user._id,
      mpTeam: null
    }));

    return [...realParticipants, ...fillerParticipants];
  };

  // Get participants grouped by team for MP team games
  const getParticipantsByTeam = () => {
    if (!isMpTeamGame) return null;
    const all = getAllParticipants();
    return {
      team1: all.filter(p => p.mpTeam === 1),
      team2: all.filter(p => p.mpTeam === 2),
      unassigned: all.filter(p => !p.mpTeam)
    };
  };

  const allParticipants = getAllParticipants();

  const getCardClasses = () => {
    const baseClasses = ['tournament-card-compact', 'tournament-card-base'];
    
    if (isGiveaway) {
      baseClasses.push('tournament-card-giveaway', 'gold-glow');
    } else if (isHighPrize) {
      baseClasses.push('tournament-card-high-prize');
    } else {
      baseClasses.push(tournament.status === 'countdown' ? 'bg-red-900/20' : 'bg-green-900/20');
    }
    
    return baseClasses.join(' ');
  };

  return (
    <div className={getCardClasses()}>
      {/* Giveaway Badge - Bottom right */}
      {isGiveaway && (
        <div className="tournament-giveaway-badge">
          <span>🎁</span>
          <span>{t('giveaway', 'GIVEAWAY')}</span>
        </div>
      )}
      
      {/* Enhanced Room ID Section with Access Control */}
      {(tournament.status === 'countdown' || tournament.status === 'active') && (
        <div className="ongoing-room-section">
          {canSeeRoomId ? (
            <>
              <div className="ongoing-room-info">
                <span className="ongoing-room-label">{t('roomId', '🎮 ROOM')}:</span>
                <span className="ongoing-room-id">{tournament.matchId || t('generating', 'Generating...')}</span>
              </div>
              <div className="ongoing-room-actions">
                <button
                  onClick={() => onCopyMatchId(tournament.matchId)}
                  className="ongoing-action-btn ongoing-action-copy"
                  disabled={!tournament.matchId}
                >
                  <Copy className="w-3.5 h-3.5" />
                  <span>{t('copyId', 'Copy')}</span>
                </button>
                {tournament.roomUrl && (
                  <button
                    onClick={() => window.open(tournament.roomUrl, '_blank')}
                    className="ongoing-action-btn ongoing-action-join"
                    title={t('joinRoom', 'Join Room')}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>{t('joinRoom', 'Join')}</span>
                  </button>
                )}
                {onCancel && (
                  <button
                    onClick={() => onCancel(tournament)}
                    className="ongoing-action-btn ongoing-action-cancel"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    <span>{t('cancel', 'Cancel')}</span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="ongoing-room-denied">
              <span className="ongoing-room-denied-icon">🔒</span>
              <span className="ongoing-room-denied-text">{t('noAccess', 'No Access')} - {t('notInTournament', 'Not in this tournament')}</span>
            </div>
          )}
        </div>
      )}

      <div className="tournament-card-compact-main">
        <div className="tournament-card-compact-left">
          <div className="tournament-card-compact-title" style={{ color: isGiveaway ? '#fef3c7' : isHighPrize ? '#fecaca' : 'white' }}>
            {getTitleDisplay(tournament)}
            {getCustomModeWarning(tournament) && (
              <span className="hidden sm:inline text-red-400 font-bold ml-2 text-xs">
                ⚠️{getCustomModeWarning(tournament)}
              </span>
            )}
          </div>
          
          <div className="tournament-card-compact-details">
            <span className="text-red-400">⚔️{getModeDisplay(tournament)}</span>
            <span className="text-blue-400">📍{tournament.map}</span>
            <span className="text-yellow-400">👥{actualParticipantCount}/{tournament.maxPlayers}</span>
            {fillers.length > 0 && (
              <span className="text-blue-400">🤖{fillers.length}</span>
            )}
            <span className={`font-bold ${isGiveaway ? 'text-yellow-400 animate-pulse' : isHighPrize ? 'text-red-400' : 'text-green-400'}`}>
              🏆{tournament.prizePool}
            </span>
            {tournament.status === 'countdown' && countdown && (
              <span className="text-red-400 font-black animate-pulse">⏱️{countdown}</span>
            )}
          </div>
        </div>

        <div className="tournament-card-compact-right">
          <div className="tournament-card-compact-actions">
            <div className={`tournament-status-base ${
              tournament.status === 'countdown' 
                ? 'tournament-status-standby' 
                : isGiveaway ? 'tournament-status-lobby-giveaway tournament-status-active' :
                  isHighPrize ? 'tournament-status-lobby-high-prize' :
                  'tournament-status-active'
            }`}>
              {tournament.status === 'countdown' ? t('standby', 'STANDBY') : t('active', 'ACTIVE')}
            </div>

            {isParticipant && (
              <div className="tournament-status-base bg-green-600/20 border-green-600 text-green-400">
                ✓
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom section with team */}
      <div className="tournament-card-compact-bottom">
        <div className="tournament-card-compact-team">
          {/* For MP Team Games - Show Team 1 vs Team 2 */}
          {isMpTeamGame ? (
            <>
              <div className="mp-teams-display">
                {/* Team 1 */}
                <div className="mp-team-section team-1">
                  <span className="mp-team-label" style={{ color: '#60a5fa' }}>
                    {t('team', 'Team')} 1
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {getParticipantsByTeam()?.team1.map(participant => (
                      <span
                        key={participant._id}
                        className={`tournament-participant-badge ${
                          participant.type === 'filler' ? 'tournament-participant-filler' :
                          participant.isCurrentUser ? 'tournament-participant-self' : 'tournament-participant-other'
                        }`}
                      >
                        {participant.type === 'filler' ? '🤖' : '👤'}{participant.name}
                      </span>
                    ))}
                    {Array.from({ length: Math.ceil(tournament.maxPlayers / 2) - (getParticipantsByTeam()?.team1.length || 0) }).map((_, idx) => (
                      <span key={`empty-t1-${idx}`} className="tournament-participant-empty">👤---</span>
                    ))}
                  </div>
                </div>

                {/* VS Divider */}
                <div className="mp-vs-divider">
                  <span style={{ color: '#f97316', fontWeight: 'bold' }}>VS</span>
                </div>

                {/* Team 2 */}
                <div className="mp-team-section team-2">
                  <span className="mp-team-label" style={{ color: '#f87171' }}>
                    {t('team', 'Team')} 2
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {getParticipantsByTeam()?.team2.map(participant => (
                      <span
                        key={participant._id}
                        className={`tournament-participant-badge ${
                          participant.type === 'filler' ? 'tournament-participant-filler' :
                          participant.isCurrentUser ? 'tournament-participant-self' : 'tournament-participant-other'
                        }`}
                      >
                        {participant.type === 'filler' ? '🤖' : '👤'}{participant.name}
                      </span>
                    ))}
                    {Array.from({ length: Math.ceil(tournament.maxPlayers / 2) - (getParticipantsByTeam()?.team2.length || 0) }).map((_, idx) => (
                      <span key={`empty-t2-${idx}`} className="tournament-participant-empty">👤---</span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : isBRDuoOrSquad ? (
            /* BR Duo/Squad - Show Teams Grid */
            <div className="br-teams-display">
              <div className="br-teams-header">
                <span className="br-teams-label">
                  🎮 {tournament.multiplayerType === 'duo' ? t('duoTeams', 'Duo Teams') : t('squadTeams', 'Squad Teams')}
                </span>
                {tournament.brTeamSettings?.mode && (
                  <span className={`br-mode-badge br-mode-${tournament.brTeamSettings.mode}`}>
                    {tournament.brTeamSettings.mode === 'solo' ? '👤 Solo' :
                     tournament.brTeamSettings.mode === 'random' ? '🎲 Random' : '👥 Squad'}
                  </span>
                )}
              </div>
              <div className="br-teams-grid-compact">
                {getBRTeams()?.filter(team => team.members.length > 0).map(team => (
                  <div key={team.teamNumber} className={`br-team-compact ${
                    team.members.some(m => m._id === user._id) ? 'br-team-mine' : ''
                  }`}>
                    <span className="br-team-num">T{String(team.teamNumber).padStart(2, '0')}</span>
                    <div className="br-team-members-compact">
                      {team.members.map(member => (
                        <span
                          key={member._id}
                          className={`br-member-badge ${member._id === user._id ? 'br-member-self' : ''}`}
                          title={member.name}
                        >
                          {member.isFiller ? '🤖' : '👤'}{member.name}
                        </span>
                      ))}
                      {Array.from({ length: team.spotsRemaining }).map((_, idx) => (
                        <span key={`empty-${idx}`} className="br-member-empty">---</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Default - Show Participants Button */
            <div className="tournament-participants-summary">
              <button
                onClick={() => setShowPlayersModal(true)}
                className="show-participants-btn"
              >
                👥 {t('showParticipants', 'Show Participants')} ({actualParticipantCount}/{tournament.maxPlayers})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Regular Participants Modal - For Solo/FFA tournaments */}
      {showPlayersModal && !isBRDuoOrSquad && !isMpTeamGame && ReactDOM.createPortal(
        <div className="participants-modal-overlay" onClick={() => setShowPlayersModal(false)}>
          <div className="participants-modal" onClick={e => e.stopPropagation()}>
            <div className="participants-modal-header">
              <h3>👥 {t('participants', 'Participants')} ({actualParticipantCount}/{tournament.maxPlayers})</h3>
              <button
                className="participants-modal-close"
                onClick={() => setShowPlayersModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="participants-modal-content">
              <div className="participants-grid-layout" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px" }}>
                {allParticipants.map(participant => (
                  <span
                    key={participant._id}
                    className={`tournament-participant-badge ${
                      participant.type === 'filler' ? 'tournament-participant-filler' :
                      participant.isCurrentUser
                        ? 'tournament-participant-self'
                        : 'tournament-participant-other'
                    }`}
                  >
                    {participant.type === 'filler' ? '🤖' : '👤'}
                    {participant.name}
                    {participant.isCurrentUser && ` (${t('you', 'You')})`}
                    {participant.type === 'filler' && participant.isMyFiller && (
                      <span className="text-xs opacity-75"> (Your Filler)</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Enhanced FinishedTournamentCard with Tournament ID display, participants, approved by badge, RTL support, FILLER SUPPORT, and TEAM DISPLAY
const FinishedTournamentCard = ({ tournament, user, onCopyTournamentId, getModeDisplay, getCustomModeDisplay, getTitleDisplay, getCustomModeWarning, cleanAdminName, t }) => {
  const [showParticipants, setShowParticipants] = useState(false);
  const isWinner = tournament.winners?.some(w => w._id === user._id);
  const isGiveaway = tournament.tournamentType === 'giveaway';

  // Stake level tiers based on total prize
  const totalPrize = tournament.prizePool || 0;
  const stakeLevel = totalPrize >= 100000 ? 'legendary' :
                     totalPrize >= 50000 ? 'veryHigh' :
                     totalPrize >= 20000 ? 'high' : null;
  const isHighPrize = stakeLevel !== null;
  const isCancelled = tournament.status === 'cancelled';
  const isAutoCancelled = tournament.cancellationInfo?.reason?.includes('Auto-cancelled');

  // Include fillers in participant display
  const realParticipants = tournament.participants || [];
  const fillers = tournament.fillers || [];
  const totalParticipants = realParticipants.length + fillers.length;
  const actualParticipantCount = tournament.actualParticipantCount || totalParticipants;

  // Check if this is a multiplayer team game
  const teamGameModes = ['teamDeathmatch', 'domination', 'searchAndDestroy', 'hardpoint',
                         'killConfirmed', 'captureTheFlag', 'frontline', 'cranked'];
  const isMpTeamGame = teamGameModes.includes(tournament.gameMode) && tournament.multiplayerType === 'team';

  // Check if this is a BR Duo/Squad tournament
  const isBRDuoOrSquad = tournament.gameMode === 'battleRoyale' &&
                          ['duo', 'squad'].includes(tournament.multiplayerType);

  // Helper function to get user's MP team assignment
  const getUserMpTeam = (userId) => {
    if (!isMpTeamGame || !tournament.mpTeamAssignments) return null;
    const assignment = tournament.mpTeamAssignments.find(
      a => (a.userId?._id || a.userId)?.toString() === userId?.toString()
    );
    return assignment?.teamNumber || null;
  };

  // Calculate BR teams
  const getBRTeams = () => {
    if (!isBRDuoOrSquad) return null;

    const maxTeamSize = tournament.multiplayerType === 'duo' ? 2 : 4;
    const teamCount = Math.ceil(tournament.maxPlayers / maxTeamSize);
    const teams = [];

    for (let i = 0; i < teamCount; i++) {
      const teamNumber = i + 1;
      const teamMembers = (tournament.brTeamAssignments || [])
        .filter(a => a.teamNumber === teamNumber)
        .map(a => ({
          _id: (a.userId?._id || a.userId)?.toString(),
          name: a.inGameName || a.username || 'Unknown',
          preference: a.preference,
          isFiller: a.isFiller
        }));

      teams.push({
        teamNumber,
        members: teamMembers,
        spotsRemaining: maxTeamSize - teamMembers.length
      });
    }

    return teams;
  };

  // Helper function to get all participants including fillers
  const getAllParticipants = () => {
    const realParticipants = tournament.participants.map(p => ({
      _id: p._id,
      type: 'real',
      name: cleanAdminName(p.inGameName || p.username || 'Unknown'),
      isCurrentUser: p._id === user._id,
      isWinner: !isCancelled && tournament.winners?.some(w => w._id === p._id),
      mpTeam: getUserMpTeam(p._id)
    }));

    const fillerParticipants = fillers.map(f => ({
      _id: f._id,
      type: 'filler',
      name: f.name,
      addedBy: f.addedBy,
      isCurrentUser: false,
      isMyFiller: f.addedBy === user._id,
      isWinner: !isCancelled && tournament.winners?.some(w => w._id === f._id),
      mpTeam: null
    }));

    return [...realParticipants, ...fillerParticipants];
  };

  // Get participants grouped by team for MP team games
  const getParticipantsByTeam = () => {
    if (!isMpTeamGame) return null;
    const all = getAllParticipants();
    return {
      team1: all.filter(p => p.mpTeam === 1),
      team2: all.filter(p => p.mpTeam === 2),
      unassigned: all.filter(p => !p.mpTeam)
    };
  };

  const allParticipants = getAllParticipants();
  
  // Helper function to clean names and get role info
  const getApprovedByInfo = (approvedBy) => {
    if (!approvedBy) return null;
    
    const originalName = approvedBy.inGameName || approvedBy.username || 'Unknown';
    
    // Clean the name by removing admin suffixes
    const cleanName = originalName
      .replace(/APPROVER$/i, '')
      .replace(/ADMIN$/i, '')
      .replace(/SUPERVISOR$/i, '')
      .replace(/_approver$/i, '')
      .replace(/_admin$/i, '')
      .replace(/_supervisor$/i, '')
      .replace(/\(approver\)$/i, '')
      .replace(/\(admin\)$/i, '')
      .replace(/\(supervisor\)$/i, '')
      .replace(/\[approver\]$/i, '')
      .replace(/\[admin\]$/i, '')
      .replace(/\[supervisor\]$/i, '')
      .trim();
    
    // Determine role and styling
    const role = approvedBy.role?.toLowerCase() || 'admin';
    let roleDisplay, roleClass, roleIcon;
    
    switch (role) {
      case 'main_admin':
        roleDisplay = 'MAIN ADMIN';
        roleClass = 'main-admin';
        roleIcon = '👑';
        break;
      case 'supervisor':
        roleDisplay = 'SUPERVISOR';
        roleClass = 'supervisor';
        roleIcon = '⭐';
        break;
      case 'approver':
        roleDisplay = 'APPROVER';
        roleClass = 'approver';
        roleIcon = '✅';
        break;
      default:
        roleDisplay = 'ADMIN';
        roleClass = 'admin';
        roleIcon = '🛡️';
    }
    
    return {
      cleanName,
      originalName,
      role,
      roleDisplay,
      roleClass,
      roleIcon
    };
  };
  
  const getCardClasses = () => {
    const baseClasses = ['tournament-card-compact', 'tournament-card-base'];
    
    if (isCancelled) {
      if (isAutoCancelled) {
        baseClasses.push('tournament-card-auto-cancelled');
      } else {
        baseClasses.push('tournament-card-cancelled');
      }
    } else if (isGiveaway) {
      baseClasses.push('bg-gradient-to-br from-yellow-900/30 via-gray-800/40 to-yellow-900/30 border-4 border-yellow-500/50 shadow-lg shadow-yellow-500/20');
    } else if (isHighPrize) {
      baseClasses.push('bg-gradient-to-br from-red-900/20 via-gray-800/40 to-red-900/20 shadow-lg shadow-red-500/10');
    } else {
      baseClasses.push('bg-gray-800/40');
    }
    
    return baseClasses.join(' ');
  };
  
  const getStatusComponent = () => {
    if (isCancelled) {
      if (isAutoCancelled) {
        return (
          <div className="tournament-status-base tournament-status-auto-cancelled">
            {t('autoCanceled', 'AUTO-CANCELED')}
          </div>
        );
      } else {
        return (
          <div className="tournament-status-base tournament-status-cancelled">
            {t('canceled', 'CANCELED')}
          </div>
        );
      }
    } else {
      // Completed tournament
      if (isGiveaway) {
        return (
          <div className="tournament-status-base tournament-status-completed-giveaway">
            {t('completed', 'COMPLETED')}
          </div>
        );
      } else {
        return (
          <div className="tournament-status-base tournament-status-completed">
            {t('completed', 'COMPLETED')}
          </div>
        );
      }
    }
  };

  const getCompletionDate = () => {
    if (isCancelled && tournament.cancellationInfo?.cancelledAt) {
      return new Date(tournament.cancellationInfo.cancelledAt).toLocaleDateString();
    } else if (tournament.completedAt) {
      return new Date(tournament.completedAt).toLocaleDateString();
    }
    return new Date(tournament.createdAt).toLocaleDateString();
  };

  // Format tournament ID for display (show last 8 characters)
  const getFormattedTournamentId = (id) => {
    if (!id) return 'N/A';
    return id.length > 8 ? `${id.slice(-8)}` : id;
  };
  
  return (
    <div className={getCardClasses()}>
      {/* Border effects */}
      {isCancelled && (
        <>
          <div className="tournament-border-cancelled-top"></div>
          <div className="tournament-border-cancelled-bottom"></div>
        </>
      )}
      {!isCancelled && isGiveaway && (
        <>
          <div className="tournament-border-glow-top" style={{ opacity: 0.5 }}></div>
          <div className="tournament-border-glow-top-secondary" style={{ opacity: 0.5 }}></div>
          <div className="tournament-border-glow-bottom" style={{ opacity: 0.5 }}></div>
          <div className="tournament-border-glow-bottom-secondary" style={{ opacity: 0.5 }}></div>
        </>
      )}
      {!isCancelled && stakeLevel && !isGiveaway && (
        <>
          <div className={`tournament-border-stakes-top tournament-border-stakes-${stakeLevel}`} style={{ opacity: 0.5 }}></div>
          <div className={`tournament-border-stakes-bottom tournament-border-stakes-${stakeLevel}`} style={{ opacity: 0.5 }}></div>
        </>
      )}

      {/* Tournament ID Section at the top */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-700/50">
        <div className="flex items-center gap-2">
          <Hash className="w-3 h-3 text-blue-400" />
          <span className="text-blue-400 font-bold text-xs rtl-tournament-id">
            {t('tournamentId', 'ID')}: {getFormattedTournamentId(tournament._id)}
          </span>
        </div>
        <button 
          onClick={() => onCopyTournamentId(tournament._id)}
          className="tournament-btn-base tournament-btn-copy text-xs px-2 py-1"
          title={t('copyTournamentId', 'Copy Tournament ID')}
        >
          <Copy className="w-3 h-3" />
          {t('copyId', 'COPY')}
        </button>
      </div>

      {/* Approved By Badge with Role-Based Styling */}
      {tournament.approvedBy && (() => {
        const approvedInfo = getApprovedByInfo(tournament.approvedBy);
        if (!approvedInfo) return null;
        
        return (
          <div className="approved-by-section">
            <div className="approved-by-info">
              <div className="approved-by-label">
                <Shield className="w-4 h-4" />
                {t('approvedBy', 'Approved by')}:
              </div>
              <span className="approved-by-name">
                {approvedInfo.cleanName}
              </span>
            </div>
            <div className={`approved-by-badge ${approvedInfo.roleClass}`}>
              <span>{approvedInfo.roleIcon}</span>
              <span>{approvedInfo.roleDisplay}</span>
            </div>
          </div>
        );
      })()}

      <div className="tournament-card-compact-main">
        <div className="tournament-card-compact-left">
          <div className="tournament-card-compact-title" style={{ 
            color: isCancelled ? '#fca5a5' : 
                   isGiveaway ? '#fef3c7' : 
                   isHighPrize ? '#fecaca' : 'white' 
          }}>
            {getTitleDisplay(tournament)}
            {getCustomModeWarning(tournament) && (
              <span className="hidden sm:inline text-red-400 font-bold ml-2 text-xs">
                ⚠️{getCustomModeWarning(tournament)}
              </span>
            )}
          </div>
          
          <div className="tournament-card-compact-details">
            <span className="text-red-400">⚔️{getModeDisplay(tournament)}</span>
            <span className="text-blue-400">📍{tournament.map}</span>
            <span className="text-yellow-400">👥{actualParticipantCount}/{tournament.maxPlayers}</span>
            {fillers.length > 0 && (
              <span className="text-blue-400">🤖{fillers.length}</span>
            )}
            <span className={`font-bold ${
              isCancelled ? 'text-gray-400' :
              isGiveaway ? 'text-yellow-400' : 
              isHighPrize ? 'text-red-400' : 'text-green-400'
            }`}>
              🏆{tournament.prizePool}
            </span>
            <span className="tournament-card-compact-time">📅{getCompletionDate()}</span>
          </div>
          
          {/* Cancellation reason display */}
          {isCancelled && tournament.cancellationInfo?.reason && (
            <div className="tournament-cancellation-reason">
              <div className="tournament-cancellation-reason-text">
                {tournament.cancellationInfo.reason}
              </div>
              {tournament.cancellationInfo.cancelledBy && (
                <div className="tournament-cancellation-meta">
                  {t('cancelledBy', 'Cancelled by')}: {cleanAdminName(tournament.cancellationInfo.cancelledBy.username || tournament.cancellationInfo.cancelledBy.inGameName || 'System')}
                </div>
              )}
              {/* Debug info for main_admin only */}
              {user?.role === 'main_admin' && tournament.cancellationInfo.debugInfo && (
                <div className="tournament-cancellation-debug" style={{ 
                  fontSize: '0.7rem', 
                  marginTop: '4px', 
                  padding: '4px', 
                  backgroundColor: 'rgba(255,165,0,0.1)', 
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Debug Info:</div>
                  {tournament.cancellationInfo.debugInfo.ipAddress && (
                    <div>IP: {tournament.cancellationInfo.debugInfo.ipAddress}</div>
                  )}
                  {tournament.cancellationInfo.debugInfo.userAgent && (
                    <div style={{ wordBreak: 'break-all' }}>UA: {tournament.cancellationInfo.debugInfo.userAgent.substring(0, 60)}...</div>
                  )}
                  {tournament.cancellationInfo.debugInfo.requestTime && (
                    <div>Time: {new Date(tournament.cancellationInfo.debugInfo.requestTime).toLocaleString()}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="tournament-card-compact-right">
          {getStatusComponent()}
        </div>
      </div>

      {/* Bottom section - Show Participants button */}
      <div className="tournament-card-compact-bottom">
        <button
          onClick={() => setShowParticipants(true)}
          className="show-participants-btn"
        >
          👥 {t('showParticipants', 'Show Participants')} ({actualParticipantCount})
        </button>
      </div>

      {/* Participants Modal */}
      {showParticipants && ReactDOM.createPortal(
        <div className="participants-modal-overlay" onClick={() => setShowParticipants(false)}>
          <div className="participants-modal" onClick={e => e.stopPropagation()}>
            <div className="participants-modal-header">
              <h3>👥 {t('participants', 'Participants')} ({actualParticipantCount})</h3>
              <button
                className="participants-modal-close"
                onClick={() => setShowParticipants(false)}
              >
                ✕
              </button>
            </div>
            <div className="participants-modal-content">
              {/* For MP Team Games - Show Team 1 vs Team 2 */}
              {isMpTeamGame ? (
            <>
              <div className="mp-teams-display">
                {/* Team 1 */}
                <div className="mp-team-section team-1">
                  <span className="mp-team-label" style={{ color: '#60a5fa' }}>
                    {t('team', 'Team')} 1
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {getParticipantsByTeam()?.team1.map(participant => {
                      const isUserSelf = participant.isCurrentUser;
                      return (
                        <span
                          key={participant._id}
                          className={`tournament-participant-badge ${
                            isCancelled ? (
                              participant.type === 'filler' ? 'tournament-participant-filler' : 'tournament-participant-other'
                            ) :
                            participant.isWinner ? (
                              isGiveaway ? 'tournament-participant-winner' :
                              isHighPrize ? 'tournament-participant-winner-high-prize' :
                              'tournament-participant-winner'
                            ) :
                            participant.type === 'filler' ? 'tournament-participant-filler' :
                            isUserSelf ? 'tournament-participant-self' : 'tournament-participant-other'
                          }`}
                          style={{ opacity: isCancelled ? 0.6 : 1 }}
                        >
                          {participant.type === 'filler' ? '🤖' : '👤'}{participant.name}
                          {participant.isWinner && !isCancelled && '👑'}
                        </span>
                      );
                    })}
                    {Array.from({ length: Math.ceil(tournament.maxPlayers / 2) - (getParticipantsByTeam()?.team1.length || 0) }).map((_, idx) => (
                      <span key={`empty-t1-${idx}`} className="tournament-participant-empty" style={{ opacity: isCancelled ? 0.4 : 0.6 }}>👤---</span>
                    ))}
                  </div>
                </div>

                {/* VS Divider */}
                <div className="mp-vs-divider">
                  <span style={{ color: isCancelled ? '#666' : '#f97316', fontWeight: 'bold' }}>VS</span>
                </div>

                {/* Team 2 */}
                <div className="mp-team-section team-2">
                  <span className="mp-team-label" style={{ color: '#f87171' }}>
                    {t('team', 'Team')} 2
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {getParticipantsByTeam()?.team2.map(participant => {
                      const isUserSelf = participant.isCurrentUser;
                      return (
                        <span
                          key={participant._id}
                          className={`tournament-participant-badge ${
                            isCancelled ? (
                              participant.type === 'filler' ? 'tournament-participant-filler' : 'tournament-participant-other'
                            ) :
                            participant.isWinner ? (
                              isGiveaway ? 'tournament-participant-winner' :
                              isHighPrize ? 'tournament-participant-winner-high-prize' :
                              'tournament-participant-winner'
                            ) :
                            participant.type === 'filler' ? 'tournament-participant-filler' :
                            isUserSelf ? 'tournament-participant-self' : 'tournament-participant-other'
                          }`}
                          style={{ opacity: isCancelled ? 0.6 : 1 }}
                        >
                          {participant.type === 'filler' ? '🤖' : '👤'}{participant.name}
                          {participant.isWinner && !isCancelled && '👑'}
                        </span>
                      );
                    })}
                    {Array.from({ length: Math.ceil(tournament.maxPlayers / 2) - (getParticipantsByTeam()?.team2.length || 0) }).map((_, idx) => (
                      <span key={`empty-t2-${idx}`} className="tournament-participant-empty" style={{ opacity: isCancelled ? 0.4 : 0.6 }}>👤---</span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : isBRDuoOrSquad ? (
            /* BR Duo/Squad - Show Teams Grid */
            <div className="br-teams-display">
              <div className="br-teams-header">
                <span className="br-teams-label">
                  🎮 {tournament.multiplayerType === 'duo' ? t('duoTeams', 'Duo Teams') : t('squadTeams', 'Squad Teams')}
                </span>
                {tournament.brTeamSettings?.mode && (
                  <span className={`br-mode-badge br-mode-${tournament.brTeamSettings.mode}`}>
                    {tournament.brTeamSettings.mode === 'solo' ? '👤 Solo' :
                     tournament.brTeamSettings.mode === 'random' ? '🎲 Random' : '👥 Squad'}
                  </span>
                )}
              </div>
              <div className="br-teams-grid-compact">
                {getBRTeams()?.filter(team => team.members.length > 0).map(team => (
                  <div key={team.teamNumber} className={`br-team-compact ${
                    team.members.some(m => m._id === user._id) ? 'br-team-mine' : ''
                  }`} style={{ opacity: isCancelled ? 0.6 : 1 }}>
                    <span className="br-team-num">T{String(team.teamNumber).padStart(2, '0')}</span>
                    <div className="br-team-members-compact">
                      {team.members.map(member => {
                        const memberIsWinner = !isCancelled && tournament.winners?.some(w => w._id?.toString() === member._id);
                        return (
                          <span
                            key={member._id}
                            className={`br-member-badge ${member._id === user._id ? 'br-member-self' : ''} ${memberIsWinner ? 'br-member-winner' : ''}`}
                            title={member.name}
                          >
                            {member.isFiller ? '🤖' : '👤'}{member.name}
                            {memberIsWinner && '👑'}
                          </span>
                        );
                      })}
                      {Array.from({ length: team.spotsRemaining }).map((_, idx) => (
                        <span key={`empty-${idx}`} className="br-member-empty">---</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Default - Simple participant list */
            <>
              <span className="tournament-card-compact-team-label">
                {isCancelled ? t('participants', 'Participants') : t('players', 'Players')}:
              </span>
              <div className="participants-grid-layout" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px" }}>
                {allParticipants.map(participant => {
                  const isUserSelf = participant.isCurrentUser;

                  return (
                    <span
                      key={participant._id}
                      className={`tournament-participant-badge ${
                        isCancelled ? (
                          participant.type === 'filler' ? 'tournament-participant-filler' : 'tournament-participant-other'
                        ) :
                        participant.isWinner ? (
                          isGiveaway ? 'tournament-participant-winner' :
                          isHighPrize ? 'tournament-participant-winner-high-prize' :
                          'tournament-participant-winner'
                        ) :
                        participant.type === 'filler' ? 'tournament-participant-filler' :
                        isUserSelf ? 'tournament-participant-self' : 'tournament-participant-other'
                      }`}
                      style={{ opacity: isCancelled ? 0.6 : 1 }}
                    >
                      {participant.type === 'filler' ? '🤖' : '👤'}
                      {participant.name}
                      {participant.isWinner && !isCancelled && '👑'}
                      {isUserSelf && ` (${t('you', 'You')})`}
                      {participant.type === 'filler' && participant.isMyFiller && !isUserSelf && (
                        <span className="text-xs opacity-75"> (Your Filler)</span>
                      )}
                    </span>
                  );
                })}
              </div>
            </>
          )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// FIXED: FillerManagement component with complete giveaway exclusion
const FillerManagement = ({ 
  tournament, 
  user, 
  onTournamentUpdate, 
  onUserUpdate, 
  onOpenFillerModal,
  cleanAdminName,
  t 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  // CRITICAL: Completely hide filler management for giveaway tournaments
  if (tournament.tournamentType === 'giveaway') {
    return null;
  }

  const fillers = tournament.fillers || [];
  const isCreator = tournament.creator._id === user._id;
  const isAdmin = ['main_admin', 'manager', 'supervisor', 'approver'].includes(user.role);
  const canManageFillers = isCreator || isAdmin;

  // Check if this is a team-based tournament
  const isBRTeamTournament = tournament.gameMode === 'battleRoyale' &&
    ['duo', 'squad'].includes(tournament.multiplayerType);
  const isMPTeamTournament = tournament.gameMode === 'multiplayer' &&
    tournament.maxPlayers > 2;
  const isTeamTournament = isBRTeamTournament || isMPTeamTournament;

  // For solo BR, don't allow fillers
  const isSoloBR = tournament.gameMode === 'battleRoyale' &&
    tournament.multiplayerType === 'solo';

  // Get user's team assignment
  let userTeamNumber = null;
  if (isBRTeamTournament && tournament.brTeamAssignments) {
    const brAssignment = tournament.brTeamAssignments.find(
      a => (a.userId?._id || a.userId)?.toString() === user._id?.toString()
    );
    userTeamNumber = brAssignment?.teamNumber || null;
  } else if (isMPTeamTournament && tournament.mpTeamAssignments) {
    const mpAssignment = tournament.mpTeamAssignments.find(
      a => (a.userId?._id || a.userId)?.toString() === user._id?.toString()
    );
    userTeamNumber = mpAssignment?.teamNumber || null;
  }

  // Check if user's team has available spots
  let teamHasSpace = true;
  if (isTeamTournament && userTeamNumber) {
    const maxTeamSize = isBRTeamTournament
      ? (tournament.multiplayerType === 'duo' ? 2 : 4)
      : Math.ceil(tournament.maxPlayers / 2);
    const teamAssignments = isBRTeamTournament
      ? tournament.brTeamAssignments || []
      : tournament.mpTeamAssignments || [];
    const teamMembers = teamAssignments.filter(a => a.teamNumber === userTeamNumber);
    teamHasSpace = teamMembers.length < maxTeamSize;
  }

  // Can add filler if:
  // 1. Tournament is waiting
  // 2. Tournament is not full
  // 3. User can manage fillers
  // 4. NOT a solo BR tournament
  // 5. For team tournaments: user must be in a team with available spots
  const canAddFiller = tournament.status === 'waiting' &&
                     !tournament.isActuallyFull &&
                     canManageFillers &&
                     !isSoloBR &&
                     (!isTeamTournament || (userTeamNumber && teamHasSpace));

  const removeFiller = async (fillerId) => {
    if (loading) return;
    setLoading(true);
    try {
      await axios.delete(`/api/tournaments/${tournament._id}/remove-filler/${fillerId}`);
      toast.success(t('fillerRemovedSuccessfully', 'Filler removed successfully!'));
      onTournamentUpdate();
      onUserUpdate();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToRemoveFiller', 'Failed to remove filler');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Call onOpenFillerModal with just the tournament
  const handleQuickAddFiller = () => {
    if (onOpenFillerModal) {
      onOpenFillerModal(tournament);
    }
  };

  if (fillers.length === 0 && !canManageFillers) {
    return null;
  }

  return (
    <div className="filler-management-section">
      <div className="filler-management-header">
        <div className="filler-management-title">
          <span className="filler-management-icon">🤖</span>
          <span className="filler-management-label">
            {t('fillerManagement', 'FILLER MANAGEMENT')} ({fillers.length})
          </span>
        </div>
        
        <div className="filler-management-actions">
          {canAddFiller && (
            <button
              onClick={handleQuickAddFiller}
              disabled={loading}
              className="filler-btn filler-btn-add"
              title={t('addFiller', 'Add Filler')}
            >
              {loading ? '...' : '➕'}
            </button>
          )}
          
          {fillers.length > 0 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="filler-btn filler-btn-expand"
              title={isExpanded ? t('collapse', 'Collapse') : t('expand', 'Expand')}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
        </div>
      </div>

      {isExpanded && fillers.length > 0 && (
        <div className="filler-management-list">
          {fillers.map(filler => {
            const isMyFiller = filler.addedBy === user._id;
            const canRemove = isMyFiller || canManageFillers;

            return (
              <div key={filler._id} className="filler-item">
                <div className="filler-item-info">
                  <span className="filler-item-name">🤖 {filler.name}</span>
                  <span className="filler-item-meta">
                    {t('addedBy', 'Added by')}: {cleanAdminName(filler.addedBy?.username || 'Unknown')}
                  </span>
                  {filler.paidAmount > 0 && (
                    <span className="filler-item-cost">
                      💰 {filler.paidAmount} {t('pts', 'pts')}
                    </span>
                  )}
                </div>
                
                <div className="filler-item-actions">
                  {canRemove && (
                    <button
                      onClick={() => {
                        if (window.confirm(t('confirmRemoveFiller', 'Remove filler "{name}"?')
                          .replace('{name}', filler.name))) {
                          removeFiller(filler._id);
                        }
                      }}
                      disabled={loading}
                      className="filler-btn filler-btn-remove"
                      title={t('removeFiller', 'Remove Filler')}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ActiveBracketMatchCard - displays an active bracket match in the ongoing tab
const ActiveBracketMatchCard = ({ match, user, onCopyRoomId, t }) => {
  const isAdmin = ['main_admin', 'manager', 'supervisor', 'approver'].includes(user?.role);
  const userId = user?._id?.toString?.() || user?._id;

  // Check if user is a participant in this match
  const isParticipant = [...(match.team1?.players || []), ...(match.team2?.players || [])]
    .some(p => {
      const playerId = p.userId?._id?.toString?.() || p.userId?.toString?.();
      return playerId === userId;
    });

  // Only show room ID to participants and admins
  const canSeeRoomId = isAdmin || isParticipant;

  return (
    <div className={`active-bracket-match-card ${isParticipant ? 'participant' : ''}`}>
      <div className="abm-header">
        <div className="abm-match-info">
          <span className="abm-match-id">{match.matchId}</span>
          <span className="abm-round">Round {match.round}</span>
        </div>
        <div className="abm-status">
          <Zap className="w-4 h-4" />
          <span>ACTIVE</span>
        </div>
      </div>

      <div className="abm-teams">
        <div className="abm-team">
          <span className="abm-team-badge">T{String(match.team1?.teamNumber).padStart(2, '0')}</span>
          <span className="abm-team-name">{match.team1?.teamName}</span>
        </div>
        <div className="abm-vs">VS</div>
        <div className="abm-team">
          <span className="abm-team-badge">T{String(match.team2?.teamNumber).padStart(2, '0')}</span>
          <span className="abm-team-name">{match.team2?.teamName}</span>
        </div>
      </div>

      <div className="abm-details">
        <div className="abm-mode">
          <Crosshair className="w-3 h-3" />
          <span>{match.gameMode}</span>
        </div>
        <div className="abm-map">
          <MapPin className="w-3 h-3" />
          <span>{match.map}</span>
        </div>
        <div className="abm-prize">
          <Trophy className="w-3 h-3" />
          <span>{match.prizePool} pts</span>
        </div>
      </div>

      {canSeeRoomId && match.roomId && (
        <div className="abm-room">
          <span className="abm-room-label">{t('roomId', 'Room ID')}:</span>
          <span className="abm-room-value">{match.roomId}</span>
          <button
            className="abm-copy-btn"
            onClick={() => onCopyRoomId(match.roomId)}
          >
            <Copy className="w-3 h-3" />
          </button>
          {match.roomUrl && (
            <button
              className="abm-join-btn"
              onClick={() => window.open(match.roomUrl, '_blank')}
              title={t('joinRoom', 'Join Room')}
            >
              <ExternalLink className="w-3 h-3" />
              {t('joinRoom', 'Join Room')}
            </button>
          )}
        </div>
      )}

      {!canSeeRoomId && (
        <div className="abm-room-locked">
          <Shield className="w-4 h-4" />
          <span>{t('roomVisibleToPlayers', 'Room ID visible to participants only')}</span>
        </div>
      )}
    </div>
  );
};


// Active Bracket Match Button - Floating button for bracket tournament matches
const ActiveBracketMatchButton = ({ match, onCopyRoomId, t }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  return (
    <>
      {/* Main Button - Fixed positioning */}
      <div className="active-match-button-container">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="active-match-main-button active"
        >
          <Zap className="w-4 h-4" />
          {t('matchLive', 'MATCH LIVE')}
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {/* Panel in Portal - separate from button */}
      {isExpanded && ReactDOM.createPortal(
        <div className="active-match-modal-overlay" onClick={() => setIsExpanded(false)}>
          <div className="active-match-details-panel" onClick={(e) => e.stopPropagation()}>
            <div className="active-match-details-header">
              <h3 className="active-match-details-title">{match.tournamentName || t('bracketMatch', 'Bracket Match')}</h3>
              <button 
                onClick={() => setIsExpanded(false)}
                className="active-match-details-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="active-match-details-content">
              {/* Room ID Section */}
              {match.roomId && (
                <div className="active-match-room-section">
                  <div className="active-match-room-header">
                    <span className="active-match-room-label">{t('roomLabel', 'Room ID')}</span>
                    <button 
                      onClick={() => onCopyRoomId(match.roomId)}
                      className="active-match-room-copy-btn"
                    >
                      <Copy className="w-3 h-3" />
                      {t('copy', 'COPY')}
                    </button>
                  </div>
                  <div className="active-match-room-id">{match.roomId}</div>
                  {match.roomUrl && (
                    <button
                      onClick={() => window.open(match.roomUrl, '_blank')}
                      className="active-match-room-join-btn"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {t('joinRoom', 'Join Room')}
                    </button>
                  )}
                </div>
              )}

              {/* Match Settings */}
              <div className="active-match-settings-section">
                <h4 className="active-match-settings-title">{t('matchSettings', 'Match Settings')}</h4>
                <div className="active-match-settings-list">
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t('matchId', 'Match')}:</span>
                    <span className="active-match-setting-value">{match.matchId} (Round {match.round})</span>
                  </div>
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t('gameMode', 'Game Mode')}:</span>
                    <span className="active-match-setting-value">{match.gameMode}</span>
                  </div>
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t('map', 'Map')}:</span>
                    <span className="active-match-setting-value">{match.map}</span>
                  </div>
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t('prizePool', 'Prize Pool')}:</span>
                    <span className="active-match-setting-value prize">{match.prizePool} {t('pts', 'pts')}</span>
                  </div>
                </div>
              </div>

              {/* Teams */}
              <div className="active-match-settings-section">
                <h4 className="active-match-settings-title">{t('teams', 'Teams')}</h4>
                <div className="abm-teams" style={{background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px'}}>
                  <div className="abm-team">
                    <span className="abm-team-badge">T{String(match.team1?.teamNumber).padStart(2, '0')}</span>
                    <span className="abm-team-name">{match.team1?.teamName}</span>
                  </div>
                  <div className="abm-vs">VS</div>
                  <div className="abm-team">
                    <span className="abm-team-badge">T{String(match.team2?.teamNumber).padStart(2, '0')}</span>
                    <span className="abm-team-name">{match.team2?.teamName}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Active BR Multistep Button Component
const ActiveBRMultistepButton = ({ tournament, onCopyRoomId, t }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const brData = tournament.brMultistepData;
  const currentRound = brData?.currentRound || 1;
  const totalRounds = brData?.totalRounds || 3;
  const roundData = brData?.rounds?.[currentRound - 1] || {};
  const type = brData?.type || "solo";
  const standings = brData?.standings || [];

  if (!isVisible || !roundData.roomId || !roundData.roomPublished) return null;

  return (
    <>
      {isExpanded && (
        <div
          className="active-match-backdrop"
          onClick={() => setIsExpanded(false)}
        />
      )}

      <div className="active-match-button-container">
        {isExpanded && ReactDOM.createPortal(
          <div className="active-match-modal-overlay" onClick={() => setIsExpanded(false)}>
            <div className="active-match-details-panel br-multistep-floating" onClick={(e) => e.stopPropagation()}>
            <div className="active-match-details-header">
              <h3 className="active-match-details-title">
                {t("brMultistepMatch", "BR Multistep")} - {t("round", "Round")} {currentRound}/{totalRounds}
              </h3>
              <button
                onClick={() => setIsExpanded(false)}
                className="active-match-details-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="active-match-details-content">
              <div className="active-match-room-section">
                <div className="active-match-room-header">
                  <span className="active-match-room-label">{t("roomLabel", "Room ID")}</span>
                  <button
                    onClick={() => onCopyRoomId(roundData.roomId)}
                    className="active-match-room-copy-btn"
                  >
                    <Copy className="w-3 h-3" />
                    {t("copy", "COPY")}
                  </button>
                </div>
                <div className="active-match-room-id">{roundData.roomId}</div>
              </div>

              <div className="active-match-settings-section">
                <h4 className="active-match-settings-title">{t("matchSettings", "Match Settings")}</h4>
                <div className="active-match-settings-list">
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t("type", "Type")}:</span>
                    <span className="active-match-setting-value">Battle Royale {type.charAt(0).toUpperCase() + type.slice(1)}</span>
                  </div>
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t("map", "Map")}:</span>
                    <span className="active-match-setting-value">{roundData.map || "Isolated"}</span>
                  </div>
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t("participants", "Participants")}:</span>
                    <span className="active-match-setting-value">{standings.length} {type === "solo" ? t("players", "Players") : t("teams", "Teams")}</span>
                  </div>
                  <div className="active-match-setting-item">
                    <span className="active-match-setting-label">{t("prizePool", "Prize Pool")}:</span>
                    <span className="active-match-setting-value prize">{tournament.prizePool || 0} {t("pts", "pts")}</span>
                  </div>
                </div>
              </div>

              {roundData.roomUrl && (
                <div className="active-match-join-section">
                  <button
                    onClick={() => window.open(roundData.roomUrl, "_blank")}
                    className="active-match-join-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t("joinRoom", "Join Room")}
                  </button>
                </div>
              )}

              <div className="active-match-team-section">
                <h4 className="active-match-team-title">{type === "solo" ? t("participants", "Participants") : t("teams", "Teams")}</h4>
                <div className="active-match-team-members">
                  {standings.slice(0, 10).map((entry, idx) => (
                    <div key={idx} className="active-match-team-member">
                      <span className="active-match-team-indicator"></span>
                      <div className="active-match-team-info">
                        <span className="active-match-team-name">
                          {type === "solo" ? entry.playerName : entry.teamName || "Team " + entry.teamNumber}
                        </span>
                        {type !== "solo" && entry.teamMembers && entry.teamMembers.length > 0 && (
                          <span className="active-match-team-members-list">
                            {entry.teamMembers.filter(m => m.userId).map(m => m.inGameName || "Unknown").join(", ")}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {standings.length > 10 && (
                    <div className="active-match-team-member">
                      <span className="active-match-team-name">+{standings.length - 10} {t("more", "more")}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
          document.body
        )}

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="active-match-main-button active br-multistep"
        >
          <Target className="w-4 h-4" />
          {t("roundLive", "ROUND")} {currentRound} {t("live", "LIVE")}
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>
    </>
  );
};

export default TournamentsList;